import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, Trophy, MapPin, Calendar as CalendarIcon, Plus, DollarSign, Users, AlertTriangle, RefreshCw, Share2, MessageCircle, Search, Loader2, Send, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import ChargeSheet from "./ChargeSheetImproved";
import ThemeToggle from "@/components/ThemeToggle";
import LogisticsForm from "@/components/LogisticsForm";
import ActiveSquadSheet from "@/components/ActiveSquadSheet";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Games({ user, logout }) {
  const [games, setGames] = useState([]);
  const [allGames, setAllGames] = useState([]); // Store all games
  const [teams, setTeams] = useState([]);
  const [reach, setReach] = useState({});
  const [activeSheet, setActiveSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [editingPublishedReport, setEditingPublishedReport] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [showChargeSheet, setShowChargeSheet] = useState(false);
  const [chargeSheetGame, setChargeSheetGame] = useState(null);
  const [chargeSheetStatuses, setChargeSheetStatuses] = useState({}); // {gameId: {exists, reference_number}}
  const [reportStatuses, setReportStatuses] = useState({}); // {gameId: {status: 'draft'|'published', id}}
  const [myChildrenTeams, setMyChildrenTeams] = useState([]); // Academy parent's children's teams
  const [viewTab, setViewTab] = useState("upcoming"); // "upcoming" or "archived"
  
  // Create game form
  const [teamId, setTeamId] = useState("");
  const [date, setDate] = useState("");
  const [location, setLocation] = useState("");
  const [opponent, setOpponent] = useState("");
  const [squadLimit, setSquadLimit] = useState("");
  const [announcing, setAnnouncing] = useState("");
  const [standbyPromotion, setStandbyPromotion] = useState("manual");
  const [logistics, setLogistics] = useState(null); // Logistics request data
  const [showLogisticsSuccess, setShowLogisticsSuccess] = useState(false);
  const [createdLogisticsData, setCreatedLogisticsData] = useState(null);
  
  // Venue autocomplete
  const [venueSearch, setVenueSearch] = useState("");
  const [venueSuggestions, setVenueSuggestions] = useState([]);
  const [showVenueSuggestions, setShowVenueSuggestions] = useState(false);
  const [selectedVenueArea, setSelectedVenueArea] = useState("");
  const [neaAreas, setNeaAreas] = useState([]);
  const [showAreaOverride, setShowAreaOverride] = useState(false);
  
  // Signup dialog
  const [showSignupDialog, setShowSignupDialog] = useState(false);
  const [signupGame, setSignupGame] = useState(null);
  const [signupType, setSignupType] = useState("playing");
  const [calendarConflicts, setCalendarConflicts] = useState(null);
  const [checkingConflicts, setCheckingConflicts] = useState(false);
  
  // Game details dialog
  const [showGameDetails, setShowGameDetails] = useState(false);
  const [detailsGame, setDetailsGame] = useState(null);
  const [showAddPlayerDialog, setShowAddPlayerDialog] = useState(false);
  const [addPlayerGame, setAddPlayerGame] = useState(null);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState([]);
  const [addPlayerType, setAddPlayerType] = useState("playing");
  const [addPlayerTab, setAddPlayerTab] = useState("registered"); // "registered", "section", or "guest"
  const [guestPlayerName, setGuestPlayerName] = useState("");
  const [guestPlayerPhone, setGuestPlayerPhone] = useState("");
  const [guestPlayerEmail, setGuestPlayerEmail] = useState("");
  const [addingGuest, setAddingGuest] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [sectionMembers, setSectionMembers] = useState([]);
  const [sectionSearchTerm, setSectionSearchTerm] = useState("");
  const [loadingSectionMembers, setLoadingSectionMembers] = useState(false);
  const [selectedSectionPlayerIds, setSelectedSectionPlayerIds] = useState([]);
  const [playerSearchTerm, setPlayerSearchTerm] = useState("");
  
  // Calendar availability for squad selection
  const [gameAvailability, setGameAvailability] = useState(null);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  
  // Edit/manage game
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editGame, setEditGame] = useState(null);
  const [editTeamId, setEditTeamId] = useState("");
  const [editDate, setEditDate] = useState("");
  const [editLocation, setEditLocation] = useState("");
  const [editOpponent, setEditOpponent] = useState("");
  const [editSquadLimit, setEditSquadLimit] = useState("");
  const [editStandbyPromotion, setEditStandbyPromotion] = useState("manual");
  
  // Logistics management for existing games
  const [showLogisticsDialog, setShowLogisticsDialog] = useState(false);
  const [logisticsGame, setLogisticsGame] = useState(null);
  const [gameLogistics, setGameLogistics] = useState(null);
  const [loadingLogistics, setLoadingLogistics] = useState(false);
  const [savingLogistics, setSavingLogistics] = useState(false);
  
  // Match report form
  const [reportTitle, setReportTitle] = useState("");
  const [matchReport, setMatchReport] = useState("");
  const [reportAuthor, setReportAuthor] = useState("");
  const [scoreHome, setScoreHome] = useState("");
  const [scoreAway, setScoreAway] = useState("");
  
  // Complete game dialog
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [completeGame, setCompleteGame] = useState(null);
  const [completeScoreHome, setCompleteScoreHome] = useState("");
  const [completeScoreAway, setCompleteScoreAway] = useState("");
  const [goalScorers, setGoalScorers] = useState([]); // [{user_id, user_name, assists: [{user_id, user_name}]}]
  const [motmPlayer, setMotmPlayer] = useState("");
  const [matchNotes, setMatchNotes] = useState("");
  const [completing, setCompleting] = useState(false);
  
  // Attendance dialog
  const [showAttendanceDialog, setShowAttendanceDialog] = useState(false);
  const [attendanceGame, setAttendanceGame] = useState(null);
  const [attendanceList, setAttendanceList] = useState([]);
  const [savingAttendance, setSavingAttendance] = useState(false);
  const [addPlayerName, setAddPlayerName] = useState("");
  const [addPlayerMembership, setAddPlayerMembership] = useState("");
  
  // Edit Goal Scorers dialog (for completed games)
  const [showEditScorersDialog, setShowEditScorersDialog] = useState(false);
  const [editScorersGame, setEditScorersGame] = useState(null);
  const [editScorersData, setEditScorersData] = useState([]); // [{user_id, user_name, goals, assists}]
  const [scorerCandidates, setScorerCandidates] = useState([]); // everyone on the teamsheet
  const [scorerQuery, setScorerQuery] = useState("");
  const [scorerPickerOpen, setScorerPickerOpen] = useState(false);
  const [savingScorers, setSavingScorers] = useState(false);

  // Check if user has captain/management permissions
  // Either through primary role OR through team_roles array
  const captainRoles = ["convenor", "captain", "team_captain", "vice_captain"];
  const managementRoles = ["captain", "team_captain", "vice_captain", "manager", "team_admin"];
  
  const hasPrimaryCaptainRole = captainRoles.includes(user.role);
  const hasTeamManagementRole = (user.team_roles || []).some(tr => 
    managementRoles.includes(tr.role)
  );
  
  // User can create games if they have captain role OR any team management role
  const canCreateGames = hasPrimaryCaptainRole || hasTeamManagementRole;
  
  // Helper function to check if user can manage a specific game's team
  const canManageGameTeam = (gameTeamId) => {
    // Convenor/captain can manage all teams
    if (["convenor", "captain"].includes(user.role)) return true;
    
    // Check if user has a management role for this specific team
    const hasRoleForTeam = (user.team_roles || []).some(tr => 
      tr.team_id === gameTeamId && managementRoles.includes(tr.role)
    );
    
    // Also check if this is the user's primary team and they have a captain role
    const isPrimaryTeamCaptain = user.team_id === gameTeamId && 
      ["team_captain", "vice_captain"].includes(user.role);
    
    return hasRoleForTeam || isPrimaryTeamCaptain;
  };
  
  // Legacy: Keep isCaptain for backward compatibility (used for Create Game button)
  const isCaptain = canCreateGames;
  
  // Helper function to get all eligible scorers (including guests) sorted alphabetically
  const getEligibleScorers = (game, excludeUserId = null) => {
    if (!game?.signups) return [];
    
    const scorers = game.signups
      .filter(s => s.signup_type === 'playing')
      .map(signup => ({
        id: signup.is_guest ? signup.guest_id : signup.user_id,
        name: signup.is_guest ? signup.name : signup.user_name,
        isGuest: !!signup.is_guest,
        isSectionGuest: !!signup.is_section_guest,
        homeTeams: signup.home_teams || [],
        displayName: signup.is_guest 
          ? `${signup.name} (G)` 
          : signup.is_section_guest
            ? `${signup.user_name} (Guest)`
            : signup.user_name || signup.name
      }))
      .filter(s => s.id !== excludeUserId)
      .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
    
    return scorers;
  };
  
  // Determine user type
  // Opened from the Match Reports list: ?report=<game_id> jumps straight into
  // that game's report so a correction is two taps, not a hunt.
  useEffect(() => {
    const wanted = new URLSearchParams(window.location.search).get("report");
    if (!wanted || !games.length) return;
    const game = games.find((g) => g.id === wanted);
    if (game) {
      openReportDialog(game);
    } else {
      toast.error("That game isn't in this list — it may be archived or another team's");
    }
    window.history.replaceState({}, "", window.location.pathname);
  }, [games]);

  const isAcademyParent = user?.roles?.includes("academy_parent");
  const isSectionMember = user?.member_type === "section" || user.role === "captain" || user.role === "convenor";

  useEffect(() => {
    fetchGames();
    fetchTeams();
    fetchNeaAreas();
    fetchReach();
    if (isAcademyParent) {
      fetchMyChildrenTeams();
    }
    
    // Auto-refresh when tab becomes visible (prevents stale data)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchGames();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Fetch NEA areas for venue mapping
  const fetchNeaAreas = async () => {
    try {
      const response = await axios.get(`${API}/venues/areas`, { withCredentials: true });
      setNeaAreas(response.data.areas || []);
    } catch (error) {
      console.error("Error fetching NEA areas:", error);
    }
  };

  // Search venues for autocomplete
  const searchVenues = async (query) => {
    if (!query || query.length < 2) {
      setVenueSuggestions([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/venues/search?q=${encodeURIComponent(query)}&limit=8`, { withCredentials: true });
      setVenueSuggestions(response.data.venues || []);
      setShowVenueSuggestions(true);
    } catch (error) {
      console.error("Error searching venues:", error);
    }
  };

  // Debounced venue search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (venueSearch) {
        searchVenues(venueSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [venueSearch]);

  // Handle venue selection from autocomplete
  const selectVenue = (venue) => {
    setLocation(venue.name);
    setVenueSearch(venue.name);
    setSelectedVenueArea(venue.nea_area);
    setShowVenueSuggestions(false);
  };

  // Re-filter games when children's teams are loaded
  useEffect(() => {
    if (allGames.length > 0 && myChildrenTeams.length > 0) {
      const filteredGames = filterGamesByRole(allGames);
      setGames(filteredGames);
      fetchReportStatuses(filteredGames);
      checkChargeSheetStatuses(filteredGames);
    }
  }, [myChildrenTeams]);

  const fetchMyChildrenTeams = async () => {
    try {
      // Get parent's children
      const response = await axios.get(`${API}/academy/my-children`, { withCredentials: true });
      const children = response.data;
      
      // Extract unique team IDs from children
      const teamIds = [...new Set(children.map(child => child.team_id).filter(Boolean))];
      setMyChildrenTeams(teamIds);
    } catch (error) {
      console.error("Error fetching children's teams:", error);
    }
  };

  const filterGamesByRole = (gamesToFilter) => {
    // Admin and convenors see all games
    if (user?.role === "convenor" || user?.role === "admin") {
      return gamesToFilter;
    }

    // Academy coaches (non-section): show only games for teams they coach
    const isAcademyCoach = user?.roles?.includes("academy_coach");
    const coachTeams = user?.academy_coach_teams || [];
    if (isAcademyCoach && !isSectionMember && coachTeams.length > 0) {
      return gamesToFilter.filter(game => coachTeams.includes(game.team_id));
    }

    // Academy-only parents: show only games for their children's teams
    if (isAcademyParent && !isSectionMember) {
      return gamesToFilter.filter(game => myChildrenTeams.includes(game.team_id));
    }

    // Section members: show section games (default behavior)
    // Multi-role users (section member + academy parent/coach): show all for now
    return gamesToFilter;
  };

  // Filter games by upcoming vs archived
  const getFilteredGamesByTab = () => {
    const now = new Date();
    if (viewTab === "upcoming") {
      // Show games that are NOT archived and NOT cancelled
      const upcomingGames = games.filter(game => {
        const isArchived = game.status === "archived";
        const isCancelled = game.status === "cancelled";
        return !isArchived && !isCancelled;
      });
      
      // Sort: Incomplete games first (by date ascending), then completed games at bottom (by date descending)
      // A game is considered "complete" if:
      // 1. It has a score recorded
      // 2. It has a match report
      // 3. It has goal scorers recorded
      // 4. Its status is "completed"
      // 5. Its date has already passed (game happened in the past)
      return [...upcomingGames].sort((a, b) => {
        const now = new Date();
        const aDate = new Date(a.date);
        const bDate = new Date(b.date);
        const aInPast = aDate < now;
        const bInPast = bDate < now;
        const aHasScore = a.score_home != null && a.score_away != null;
        const bHasScore = b.score_home != null && b.score_away != null;
        const aHasReport = a.match_report_id || a.match_report;
        const bHasReport = b.match_report_id || b.match_report;
        const aHasGoalScorers = a.goal_scorers && a.goal_scorers.length > 0;
        const bHasGoalScorers = b.goal_scorers && b.goal_scorers.length > 0;
        const aIsComplete = aHasScore || aHasReport || aHasGoalScorers || a.status === "completed" || aInPast;
        const bIsComplete = bHasScore || bHasReport || bHasGoalScorers || b.status === "completed" || bInPast;
        
        // Incomplete (future) games come first
        if (aIsComplete && !bIsComplete) return 1;
        if (!aIsComplete && bIsComplete) return -1;
        
        // Within same completion status, sort by date
        if (!aIsComplete && !bIsComplete) {
          // Upcoming games: ascending (soonest first)
          return aDate - bDate;
        } else {
          // Completed/past games: descending (most recent first)
          return bDate - aDate;
        }
      });
    } else {
      // Show archived and completed games
      return games.filter(game => {
        const isArchived = game.status === "archived";
        const hasReport = game.match_report_id || game.match_report;
        return isArchived || hasReport;
      }).sort((a, b) => new Date(b.date) - new Date(a.date)); // Sort by date descending (most recent first)
    }
  };

  // Helper function to check if a game is "complete" (for separator logic)
  const isGameComplete = (game) => {
    const now = new Date();
    const gameDate = new Date(game.date);
    const inPast = gameDate < now;
    const hasScore = game.score_home != null && game.score_away != null;
    const hasReport = game.match_report_id || game.match_report;
    const hasGoalScorers = game.goal_scorers && game.goal_scorers.length > 0;
    return hasScore || hasReport || hasGoalScorers || game.status === "completed" || inPast;
  };

  const filteredGames = getFilteredGamesByTab();

  const fetchReach = async () => {
    try {
      const r = await axios.get(`${API}/section/whatsapp/reach`);
      setReach(r.data.teams || {});
    } catch (e) {
      setReach({});
    }
  };

  const fetchGames = async () => {
    try {
      // Add cache-busting parameter to ensure fresh data
      const response = await axios.get(`${API}/games`, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      setAllGames(response.data);
      
      // Apply role-based filtering
      const filteredGames = filterGamesByRole(response.data);
      setGames(filteredGames);
      
      // Fetch report statuses for games with match_report_id
      fetchReportStatuses(filteredGames);
      
      // Check charge sheet status for each game
      await checkChargeSheetStatuses(filteredGames);
    } catch (error) {
      console.error("Error fetching games:", error);
      toast.error("Failed to load games");
    } finally {
      setLoading(false);
    }
  };
  
  const fetchReportStatuses = async (gamesList) => {
    const statuses = {};
    
    for (const game of gamesList) {
      if (game.match_report_id) {
        try {
          const response = await axios.get(`${API}/match-reports/by-game/${game.id}`, { withCredentials: true });
          statuses[game.id] = {
            status: response.data.status,
            id: response.data.id
          };
        } catch (error) {
          // Report doesn't exist or error fetching
          console.error(`Error fetching report status for game ${game.id}:`, error);
        }
      }
    }
    
    setReportStatuses(statuses);
  };

  const checkChargeSheetStatuses = async (gamesList) => {
    try {
      const statuses = {};
      
      // Check each game in parallel
      await Promise.all(
        gamesList.map(async (game) => {
          try {
            const response = await axios.get(
              `${API}/games/${game.id}/charge-sheet/exists`,
              { withCredentials: true }
            );
            statuses[game.id] = response.data;
          } catch (error) {
            // If error, assume no charge sheet
            statuses[game.id] = { exists: false };
          }
        })
      );
      
      setChargeSheetStatuses(statuses);
    } catch (error) {
      console.error("Error checking charge sheet statuses:", error);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`);
      setTeams(response.data);
    } catch (error) {
      console.error("Error fetching teams:", error);
    }
  };

  const createGame = async () => {
    if (!teamId || !date || !location || !opponent) {
      toast.error("Please fill all fields");
      return;
    }

    try {
      // Get team name for the WhatsApp message
      const selectedTeam = teams.find(t => t.id === teamId);
      const teamName = selectedTeam?.name || "Team";
      
      await axios.post(`${API}/games`, {
        team_id: teamId,
        date: new Date(date).toISOString(),
        location,
        nea_area: selectedVenueArea || null,
        opponent,
        squad_limit: squadLimit ? parseInt(squadLimit) : null,
        standby_promotion: standbyPromotion,
        logistics: logistics
      }, { withCredentials: true });
      
      // If logistics was included, show success dialog with WhatsApp export
      if (logistics) {
        setCreatedLogisticsData({
          team: { name: teamName },
          game: { 
            opponent, 
            date: new Date(date).toISOString(), 
            location 
          },
          ...logistics
        });
        setShowLogisticsSuccess(true);
        toast.success("Game created and logistics request sent to staff!");
      } else {
        toast.success("Game created successfully!");
      }
      
      setShowCreateDialog(false);
      setTeamId("");
      setDate("");
      setLocation("");
      setVenueSearch("");
      setSelectedVenueArea("");
      setOpponent("");
      setSquadLimit("");
      setStandbyPromotion("manual");
      setLogistics(null);
      fetchGames();
    } catch (error) {
      console.error("Error creating game:", error);
      toast.error("Failed to create game");
    }
  };

  // Generate WhatsApp-friendly text for logistics request
  const generateWhatsAppText = (request) => {
    if (!request) return "";
    
    const game = request.game || {};
    const team = request.team || {};
    
    const gameDate = game.date ? new Date(game.date) : new Date();
    const dateStr = gameDate.toLocaleDateString("en-SG", { 
      weekday: "short", 
      day: "numeric", 
      month: "short" 
    });
    const timeStr = gameDate.toLocaleTimeString("en-SG", { 
      hour: "2-digit", 
      minute: "2-digit", 
      hour12: false 
    }) + "hrs";
    
    let text = `🏆 *LOGISTICS REQUEST*\n\n`;
    text += `*${team.name || "Team"} vs ${game.opponent || "Opponent"}*\n`;
    text += `📅 ${dateStr}\n`;
    text += `⏰ Kick off @${timeStr}\n`;
    text += `📍 ${game.location || "TBD"}\n`;
    
    if (request.request_referee) {
      text += `\n⚖️ *Referee Request:* ${request.referee_count || 3} officials\n`;
    }
    
    if (request.request_transport) {
      text += `\n🚐 *Transport Required*\n`;
      if (request.transport_notes) text += `${request.transport_notes}\n`;
    }
    
    if (request.equipment_items && request.equipment_items.length > 0) {
      const readyTime = request.equipment_ready_time ? ` (ready by ${request.equipment_ready_time})` : "";
      text += `\n🎒 *Equipment needed*${readyTime}\n`;
      request.equipment_items.forEach((item, idx) => {
        let itemText = `${idx + 1}. ${item.name}`;
        if (item.quantity > 1) itemText += ` x${item.quantity}`;
        if (item.color) itemText += ` (${item.color})`;
        if (item.notes) itemText += ` - ${item.notes}`;
        text += `${itemText}\n`;
      });
    }
    
    if (request.request_refreshments && request.refreshments_details) {
      text += `\n🥤 *Refreshments:*\n${request.refreshments_details}\n`;
    }
    
    if (request.special_instructions) {
      text += `\n📝 *Special Instructions:*\n${request.special_instructions}\n`;
    }
    
    text += `\n👤 *Contact:* ${request.contact_name || "N/A"}`;
    if (request.contact_phone) text += `\n📞 ${request.contact_phone}`;
    
    text += `\n\nThanks! 🙏`;
    
    return text;
  };

  const copyLogisticsToWhatsApp = () => {
    const text = generateWhatsAppText(createdLogisticsData);
    navigator.clipboard.writeText(text).then(() => {
      toast.success("Copied to clipboard! Paste in WhatsApp");
    }).catch(() => {
      toast.error("Failed to copy");
    });
  };

  // Generate WhatsApp-friendly squad text
  const generateSquadWhatsAppText = (game) => {
    const teamName = teams.find(t => t.id === game.team_id)?.name || 'Unknown Team';
    const gameDate = new Date(game.date);
    const dateStr = gameDate.toLocaleDateString('en-SG', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short',
      year: 'numeric'
    });
    // Extract time from the date object
    const timeStr = gameDate.toLocaleTimeString('en-SG', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    const signups = game.signups || [];
    const standby = game.standby || [];
    const declined = game.declined || [];
    // Use location field (that's what the game object uses)
    const venue = game.location || game.venue || 'TBD';
    
    let text = `⚽ *${teamName} - Game Day Squad*\n`;
    text += `━━━━━━━━━━━━━━━━━━\n`;
    text += `📅 ${dateStr} at ${timeStr}\n`;
    text += `📍 ${venue}\n`;
    if (game.opponent) text += `🆚 vs ${game.opponent}\n`;
    text += `━━━━━━━━━━━━━━━━━━\n\n`;
    
    // Main Squad
    text += `✅ *MAIN SQUAD* (${signups.length}${game.squad_limit ? `/${game.squad_limit}` : ''})\n`;
    if (signups.length === 0) {
      text += `_No players signed up yet_\n`;
    } else {
      signups.forEach((s, i) => {
        const type = s.signup_type === 'playing' ? '🏃' : '🍻';
        text += `${i + 1}. ${s.user_name} ${type}\n`;
      });
    }
    
    // Standby
    if (standby.length > 0) {
      text += `\n⏳ *STANDBY* (${standby.length})\n`;
      standby.forEach((s, i) => {
        const type = s.signup_type === 'playing' ? '🏃' : '🍻';
        text += `${i + 1}. ${s.user_name} ${type}\n`;
      });
    }
    
    // Unavailable
    if (declined.length > 0) {
      text += `\n❌ *UNAVAILABLE* (${declined.length})\n`;
      declined.forEach((d, i) => {
        text += `${i + 1}. ${d.user_name}`;
        if (d.reason) text += ` - _${d.reason}_`;
        text += `\n`;
      });
    }
    
    text += `\n━━━━━━━━━━━━━━━━━━\n`;
    text += `🏃 = Playing | 🍻 = Social/F&B\n`;
    
    return text;
  };

  const askSquadNow = async (game) => {
    if (!window.confirm(
      "Send a WhatsApp to everyone on this team now, asking YES or NO?\n\n"
      + "Use this for a late fixture — it ignores the usual reminder schedule. "
      + "Anyone who has already answered is skipped."
    )) return;
    setAnnouncing(`ask-${game.id}`);
    try {
      const r = await axios.post(`${API}/section/whatsapp/ask-squad`,
        { game_id: game.id }, { withCredentials: true });
      const d = r.data;
      if (d.skipped) {
        toast.info(d.reason === "team_not_enabled"
          ? "Section WhatsApp isn't switched on for this team yet — an admin does that in WhatsApp Settings"
          : "WhatsApp sending is switched off");
      } else if (d.sent > 0) {
        toast.success(`Asked ${d.sent} player(s)`);
      } else if (!d.members_on_sheet) {
        toast.info("Nobody is on this team's player list yet");
      } else if (!d.members_with_mobile) {
        toast.info("No one on this team has a mobile number on file");
      } else if (d.failed) {
        toast.error(d.template_approved
          ? `WhatsApp rejected it: ${d.failure}`
          : "The squad message template hasn't been approved yet, so WhatsApp blocked it");
      } else if (d.already_messaged) {
        toast.info("Everyone has already been asked about this game");
      } else if (d.already_answered) {
        toast.info("Everyone has already answered");
      } else {
        toast.info("Nothing to send");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not message the squad");
    } finally {
      setAnnouncing("");
    }
  };

  // Share squad to WhatsApp
  const announceSquad = async (game) => {
    if (!window.confirm(
      "Send each selected player their own WhatsApp telling them they're in the squad "
      + "or on standby?\n\nThis cannot post into your WhatsApp group — Meta has no group API "
      + "— so it goes to players individually. Anyone without a mobile on file is listed back "
      + "to you."
    )) return;
    setAnnouncing(game.id);
    try {
      const r = await axios.post(`${API}/section/whatsapp/announce-squad`,
        { game_id: game.id }, { withCredentials: true });
      if (r.data.skipped) {
        toast.info(r.data.reason === "team_not_enabled"
          ? "Section WhatsApp isn't switched on for this team yet"
          : "WhatsApp sending is switched off");
      } else {
        toast.success(`Sent to ${r.data.sent} of ${r.data.picked} selected player(s)`);
        if ((r.data.no_mobile || []).length) {
          toast.info(`No mobile on file: ${r.data.no_mobile.join(", ")}`);
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not announce the squad");
    } finally {
      setAnnouncing("");
    }
  };

  const shareSquadToWhatsApp = (game) => {    const text = generateSquadWhatsAppText(game);
    // Open WhatsApp with pre-filled message
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(whatsappUrl, '_blank');
  };

  // Copy squad to clipboard
  const copySquadToClipboard = (game) => {
    const text = generateSquadWhatsAppText(game);
    navigator.clipboard.writeText(text).then(() => {
      toast.success("Squad list copied! Paste in WhatsApp or any app");
    }).catch(() => {
      toast.error("Failed to copy");
    });
  };

  const openSignupDialog = async (game) => {
    setSignupGame(game);
    setSignupType("playing");
    setCalendarConflicts(null);
    setShowSignupDialog(true);
    
    // Check for calendar conflicts
    setCheckingConflicts(true);
    try {
      const response = await axios.get(`${API}/games/${game.id}/calendar-conflicts`, {
        withCredentials: true
      });
      setCalendarConflicts(response.data);
    } catch (error) {
      console.error("Error checking calendar conflicts:", error);
      setCalendarConflicts(null);
    } finally {
      setCheckingConflicts(false);
    }
  };

  const signupForGame = async () => {
    // Confirmation dialog
    const confirmMessage = signupType === 'playing' 
      ? "Are you sure you want to join this game as a PLAYING member?"
      : "Are you sure you want to join this game for Social/F&B?";
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/games/${signupGame.id}/signup`, {
        signup_type: signupType
      }, {
        withCredentials: true
      });
      toast.success(response.data.message);
      setShowSignupDialog(false);
      fetchGames();
    } catch (error) {
      console.error("Error signing up:", error);
      toast.error(error.response?.data?.detail || "Failed to sign up");
    }
  };

  const withdrawFromGame = async (gameId) => {
    // Confirmation dialog
    if (!window.confirm("Are you sure you want to withdraw from this game?\n\nNote: You may lose your spot if the squad fills up.")) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/games/${gameId}/withdraw`, {}, {
        withCredentials: true
      });
      toast.success(response.data.message);
      if (response.data.promoted_player) {
        setTimeout(() => {
          toast.info(`${response.data.promoted_player} promoted from standby`, {
            duration: 3000,
            position: 'top-right'
          });
        }, 500);
      }
      fetchGames();
    } catch (error) {
      console.error("Error withdrawing:", error);
      toast.error("Failed to withdraw");
    }
  };

  const declineGame = async (gameId) => {
    const reason = window.prompt("Reason for not being available (optional):");
    try {
      await axios.post(`${API}/games/${gameId}/decline`, { reason }, {
        withCredentials: true
      });
      toast.success("Marked as not available");
      fetchGames();
    } catch (error) {
      console.error("Error declining:", error);
      toast.error(error.response?.data?.detail || "Failed to decline");
    }
  };

  const undoDecline = async (gameId) => {
    try {
      await axios.delete(`${API}/games/${gameId}/decline`, {
        withCredentials: true
      });
      toast.success("You can now sign up for this game");
      fetchGames();
    } catch (error) {
      console.error("Error undoing decline:", error);
      toast.error("Failed to undo decline");
    }
  };

  const handlePendingSignup = async (gameId, userId, action) => {
    try {
      const response = await axios.post(
        `${API}/games/${gameId}/approve-signup/${userId}`,
        { action },
        { withCredentials: true }
      );
      toast.success(response.data.message);
      await fetchGames();
      if (detailsGame && detailsGame.id === gameId) {
        const gameResponse = await axios.get(`${API}/games/${gameId}`, { withCredentials: true });
        setDetailsGame(gameResponse.data);
      }
    } catch (error) {
      console.error("Error handling pending signup:", error);
      toast.error(error.response?.data?.detail || "Failed to process signup");
    }
  };

  const viewGameDetails = async (game) => {
    setDetailsGame(game);
    setShowGameDetails(true);
    
    // Fetch availability for captains/convenors
    if (isCaptain || user?.role === 'convenor') {
      fetchGameAvailability(game.id);
    }
  };

  const fetchGameAvailability = async (gameId) => {
    try {
      setLoadingAvailability(true);
      const response = await axios.get(`${API}/calendar-preferences/game-availability/${gameId}`, {
        withCredentials: true
      });
      setGameAvailability(response.data);
    } catch (error) {
      console.error("Error fetching availability:", error);
      // Don't show error toast - availability is optional feature
      setGameAvailability(null);
    } finally {
      setLoadingAvailability(false);
    }
  };

  const promoteStandby = async (gameId, userId) => {
    try {
      const response = await axios.post(
        `${API}/games/${gameId}/promote-standby?user_id=${userId}`, 
        { increase_limit: false },
        { withCredentials: true }
      );
      toast.success(response.data.message);
      
      // Refresh the game details
      await fetchGames();
      if (detailsGame && detailsGame.id === gameId) {
        const gameResponse = await axios.get(`${API}/games/${gameId}`, { withCredentials: true });
        setDetailsGame(gameResponse.data);
      }
    } catch (error) {
      console.error("Error promoting standby:", error);
      
      // Check if it's a squad full error (409 conflict)
      if (error.response?.status === 409 && error.response?.data?.requires_limit_increase) {
        const detail = error.response.data;
        toast.error(
          `${detail.message}`,
          {
            duration: 5000,
            action: {
              label: "Edit Game",
              onClick: () => {
                // Open edit game dialog
                toast.info("Use 'Edit Game' to increase the squad limit first");
              }
            }
          }
        );
      } else {
        toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || "Failed to promote player");
      }
    }
  };

  const openAddPlayerDialog = async (game) => {
    setAddPlayerGame(game);
    setSelectedPlayerIds([]);
    setAddPlayerType("playing");
    setPlayerSearchTerm("");
    setShowAddPlayerDialog(true);
    
    // Fetch team members and availability in parallel
    try {
      const [membersResponse] = await Promise.all([
        axios.get(`${API}/teams/${game.team_id}/details`, { withCredentials: true }),
        fetchGameAvailability(game.id)  // Also fetch availability if not already loaded
      ]);
      
      // Filter out players already in the game
      const alreadySignedUp = new Set([
        ...(game.signups || []).map(s => typeof s === 'string' ? s : (s.is_guest ? s.guest_id : s.user_id)),
        ...(game.standby || []).map(s => typeof s === 'string' ? s : (s.is_guest ? s.guest_id : s.user_id))
      ]);
      const availableMembers = (membersResponse.data.members_details || [])
        .filter(m => !alreadySignedUp.has(m.user_id))
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')); // Sort alphabetically
      setTeamMembers(availableMembers);
    } catch (error) {
      console.error("Error fetching team members:", error);
      toast.error("Failed to load team members");
    }
  };

  const togglePlayerSelection = (userId) => {
    setSelectedPlayerIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const manuallyAddPlayers = async () => {
    if (selectedPlayerIds.length === 0) {
      toast.error("Please select at least one player");
      return;
    }

    try {
      // Add all selected players
      const promises = selectedPlayerIds.map(userId =>
        axios.post(
          `${API}/games/${addPlayerGame.id}/add-player?user_id=${userId}&signup_type=${addPlayerType}`,
          {},
          { withCredentials: true }
        )
      );
      
      await Promise.all(promises);
      
      toast.success(`Added ${selectedPlayerIds.length} player(s) to squad as ${addPlayerType === 'playing' ? 'Playing' : 'Social/F&B'}`);
      setShowAddPlayerDialog(false);
      setSelectedPlayerIds([]);
      
      // Fetch the updated game data first
      await fetchGames();
      
      // Then update details dialog if open - wait a bit for state to update
      if (detailsGame && detailsGame.id === addPlayerGame.id) {
        // Fetch the specific game to ensure we have the latest data
        const gameResponse = await axios.get(`${API}/games/${addPlayerGame.id}`, { withCredentials: true });
        setDetailsGame(gameResponse.data);
      }
    } catch (error) {
      console.error("Error adding players:", error);
      toast.error(error.response?.data?.detail || "Failed to add players");
    }
  };

  const addGuestPlayer = async () => {
    if (!guestPlayerName.trim()) {
      toast.error("Please enter guest player name");
      return;
    }

    setAddingGuest(true);
    try {
      await axios.post(
        `${API}/games/${addPlayerGame.id}/add-guest`,
        {
          name: guestPlayerName,
          phone: guestPlayerPhone,
          email: guestPlayerEmail,
          signup_type: addPlayerType
        },
        { withCredentials: true }
      );

      toast.success(`Guest player "${guestPlayerName}" added to squad`);
      setGuestPlayerName("");
      setGuestPlayerPhone("");
      setGuestPlayerEmail("");
      
      // Refresh games
      await fetchGames();
      if (detailsGame && detailsGame.id === addPlayerGame.id) {
        const gameResponse = await axios.get(`${API}/games/${addPlayerGame.id}`, { withCredentials: true });
        setDetailsGame(gameResponse.data);
      }
    } catch (error) {
      console.error("Error adding guest player:", error);
      toast.error(error.response?.data?.detail || "Failed to add guest player");
    } finally {
      setAddingGuest(false);
    }
  };

  // Search section members (from other teams)
  const searchSectionMembers = async (searchQuery) => {
    if (!addPlayerGame) return;
    
    setLoadingSectionMembers(true);
    try {
      const response = await axios.get(
        `${API}/section/members/search?q=${encodeURIComponent(searchQuery)}&exclude_team_id=${addPlayerGame.team_id}`,
        { withCredentials: true }
      );
      
      // Filter out players already in the game
      const alreadySignedUp = new Set([
        ...(addPlayerGame.signups || []).map(s => typeof s === 'string' ? s : s.user_id),
        ...(addPlayerGame.standby || []).map(s => typeof s === 'string' ? s : s.user_id)
      ]);
      
      const availableMembers = (response.data || []).filter(m => !alreadySignedUp.has(m.user_id));
      setSectionMembers(availableMembers);
    } catch (error) {
      console.error("Error searching section members:", error);
      toast.error("Failed to search section members");
    } finally {
      setLoadingSectionMembers(false);
    }
  };

  // Add section players as guests
  const addSectionPlayers = async () => {
    if (selectedSectionPlayerIds.length === 0) {
      toast.error("Please select at least one player");
      return;
    }

    try {
      const promises = selectedSectionPlayerIds.map(userId =>
        axios.post(
          `${API}/games/${addPlayerGame.id}/add-section-player?user_id=${userId}&signup_type=${addPlayerType}`,
          {},
          { withCredentials: true }
        )
      );

      const results = await Promise.allSettled(promises);
      const successes = results.filter(r => r.status === 'fulfilled').length;
      const failures = results.filter(r => r.status === 'rejected').length;

      if (successes > 0) {
        toast.success(`${successes} section player(s) added to squad`);
      }
      if (failures > 0) {
        toast.error(`${failures} player(s) failed to add`);
      }

      setSelectedSectionPlayerIds([]);
      setSectionSearchTerm("");
      setSectionMembers([]);
      
      await fetchGames();
      if (detailsGame && detailsGame.id === addPlayerGame.id) {
        const gameResponse = await axios.get(`${API}/games/${addPlayerGame.id}`, { withCredentials: true });
        setDetailsGame(gameResponse.data);
      }
    } catch (error) {
      console.error("Error adding section players:", error);
      toast.error("Failed to add section players");
    }
  };

  const toggleSectionPlayerSelection = (userId) => {
    setSelectedSectionPlayerIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const removePlayerFromGame = async (gameId, userId) => {
    if (!window.confirm("Are you sure you want to remove this player from the game?")) {
      return;
    }

    try {
      const response = await axios.delete(`${API}/games/${gameId}/remove-player/${userId}`, {
        withCredentials: true
      });
      // Show success message first
      toast.success(response.data.message);
      
      // Show promoted players notification after a brief delay to prevent overlap
      if (response.data.promoted_players && response.data.promoted_players.length > 0) {
        setTimeout(() => {
          response.data.promoted_players.forEach((name, index) => {
            setTimeout(() => {
              toast.info(`${name} promoted from standby`, {
                duration: 3000,
                position: 'top-right'
              });
            }, index * 300); // Stagger toasts by 300ms each
          });
        }, 500); // Wait 500ms after success toast
      }
      
      // Fetch fresh game data
      await fetchGames();
      
      // Update the details dialog with fresh data from API
      if (detailsGame && detailsGame.id === gameId) {
        const gameResponse = await axios.get(`${API}/games/${gameId}`, { withCredentials: true });
        setDetailsGame(gameResponse.data);
      }
    } catch (error) {
      console.error("Error removing player:", error);
      toast.error(error.response?.data?.detail || "Failed to remove player");
    }
  };

  // Open logistics dialog for an existing game
  const openLogisticsDialog = async (game) => {
    setLogisticsGame(game);
    setShowLogisticsDialog(true);
    setLoadingLogistics(true);
    
    try {
      const response = await axios.get(`${API}/games/${game.id}/logistics`, {
        withCredentials: true
      });
      if (response.data.has_logistics) {
        setGameLogistics(response.data.logistics);
      } else {
        setGameLogistics(null);
      }
    } catch (error) {
      console.error("Error fetching logistics:", error);
      setGameLogistics(null);
    } finally {
      setLoadingLogistics(false);
    }
  };

  // Save logistics for an existing game
  const saveGameLogistics = async (logisticsData) => {
    if (!logisticsGame) return;
    
    setSavingLogistics(true);
    try {
      const response = await axios.post(`${API}/games/${logisticsGame.id}/logistics`, logisticsData, {
        withCredentials: true
      });
      
      if (response.data.is_update) {
        toast.success("Logistics request updated! Staff has been notified.");
      } else {
        toast.success("Logistics request submitted! Staff has been notified.");
      }
      
      // Update the game in local state
      await fetchGames();
      setShowLogisticsDialog(false);
      
      // Show WhatsApp export option
      setCreatedLogisticsData({
        teamName: teams.find(t => t.id === logisticsGame.team_id)?.name,
        date: logisticsGame.date,
        time: logisticsGame.time,
        venue: logisticsGame.venue || logisticsGame.location,
        opponent: logisticsGame.opponent,
        ...logisticsData
      });
      setShowLogisticsSuccess(true);
      
    } catch (error) {
      console.error("Error saving logistics:", error);
      toast.error(error.response?.data?.detail || "Failed to save logistics request");
    } finally {
      setSavingLogistics(false);
    }
  };

  const openEditDialog = (game) => {
    setEditGame(game);
    setEditTeamId(game.team_id);
    setEditDate(new Date(game.date).toISOString().slice(0, 16));
    setEditLocation(game.location);
    setEditOpponent(game.opponent);
    setEditSquadLimit(game.squad_limit || "");
    setEditStandbyPromotion(game.standby_promotion || "manual");
    setShowEditDialog(true);
  };

  const updateGame = async () => {
    if (!editTeamId || !editDate || !editLocation || !editOpponent) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      await axios.put(`${API}/games/${editGame.id}`, {
        team_id: editTeamId,
        date: new Date(editDate).toISOString(),
        location: editLocation,
        opponent: editOpponent,
        squad_limit: editSquadLimit ? parseInt(editSquadLimit) : null,
        standby_promotion: editStandbyPromotion
      }, { withCredentials: true });
      
      toast.success("Game updated successfully!");
      setShowEditDialog(false);
      
      // Refresh games list
      await fetchGames();
      
      // If game details dialog is open, refresh it with updated data
      if (detailsGame && detailsGame.id === editGame.id) {
        const updatedGameResponse = await axios.get(`${API}/games/${editGame.id}`, { withCredentials: true });
        setDetailsGame(updatedGameResponse.data);
      }
    } catch (error) {
      console.error("Error updating game:", error);
      const errorMessage = error.response?.data?.detail || "Failed to update game";
      toast.error(errorMessage);
    }
  };

  const deleteGame = async (gameId) => {
    if (!window.confirm("Are you sure you want to DELETE this game? This cannot be undone and will remove all signups.")) {
      return;
    }

    try {
      await axios.delete(`${API}/games/${gameId}`, {
        withCredentials: true
      });
      toast.success("Game deleted successfully");
      fetchGames();
      setShowGameDetails(false);
    } catch (error) {
      console.error("Error deleting game:", error);
      toast.error(error.response?.data?.detail || "Failed to delete game");
    }
  };

  const cancelGame = async (gameId) => {
    const reason = window.prompt("Reason for cancellation (optional):");
    if (reason === null) return; // User clicked cancel

    try {
      await axios.post(`${API}/games/${gameId}/cancel?reason=${encodeURIComponent(reason)}`, {}, {
        withCredentials: true
      });
      toast.success("Game cancelled");
      await fetchGames();
      
      // Update the details dialog with fresh data from API
      if (detailsGame && detailsGame.id === gameId) {
        const gameResponse = await axios.get(`${API}/games/${gameId}`, { withCredentials: true });
        setDetailsGame(gameResponse.data);
      }
    } catch (error) {
      console.error("Error cancelling game:", error);
      toast.error(error.response?.data?.detail || "Failed to cancel game");
    }
  };

  const openCompleteGameDialog = async (game) => {
    try {
      // Fetch full game details with players
      const response = await axios.get(`${API}/games/${game.id}`, { withCredentials: true });
      setCompleteGame(response.data);
      setCompleteScoreHome("");
      setCompleteScoreAway("");
      setGoalScorers([]);
      setMotmPlayer("");
      setMatchNotes("");
      setShowCompleteDialog(true);
    } catch (error) {
      console.error("Error fetching game details:", error);
      toast.error("Failed to load game details");
    }
  };

  const markGameCompleted = async (gameId) => {
    // Find the game to pass to dialog
    const game = games.find(g => g.id === gameId);
    if (game) {
      openCompleteGameDialog(game);
    }
  };
  
  const submitGameCompletion = async () => {
    const homeInt = parseInt(completeScoreHome);
    const awayInt = parseInt(completeScoreAway);
    
    if (isNaN(homeInt) || isNaN(awayInt) || homeInt < 0 || awayInt < 0) {
      toast.error("Please enter valid score numbers (0 or greater)");
      return;
    }
    
    if (!window.confirm(`Mark this game as completed with score ${homeInt}-${awayInt}?\n\nThis will:\n- Close signups\n- Lock game details\n- Update player stats\n\nContinue?`)) {
      return;
    }

    setCompleting(true);
    try {
      // Mark game as completed with score and goal scorers
      // goalScorers format: [{user_id, user_name, assists: [{user_id, user_name}]}]
      const formattedScorers = goalScorers.map(scorer => ({
        user_id: scorer.user_id,
        user_name: scorer.user_name,
        goals: 1,  // Each entry is 1 goal
        assists: 0
      }));
      
      // Aggregate goals by user
      const aggregatedScorers = {};
      formattedScorers.forEach(s => {
        if (!aggregatedScorers[s.user_id]) {
          aggregatedScorers[s.user_id] = { ...s, goals: 0 };
        }
        aggregatedScorers[s.user_id].goals += s.goals;
      });
      
      // Add assists from the assists array in each scorer
      goalScorers.forEach(scorer => {
        (scorer.assists || []).forEach(assist => {
          if (!aggregatedScorers[assist.user_id]) {
            aggregatedScorers[assist.user_id] = { 
              user_id: assist.user_id, 
              user_name: assist.user_name, 
              goals: 0, 
              assists: 0 
            };
          }
          aggregatedScorers[assist.user_id].assists += 1;
        });
      });
      
      const finalScorers = Object.values(aggregatedScorers);
      
      await axios.post(
        `${API}/games/${completeGame.id}/mark-completed`, 
        {
          score_home: homeInt,
          score_away: awayInt,
          goal_scorers: finalScorers
        },
        { withCredentials: true }
      );
      
      // Update MOTM separately if selected
      const validMotm = motmPlayer && motmPlayer !== "none" ? motmPlayer : null;
      if (validMotm) {
        await axios.post(
          `${API}/games/${completeGame.id}/update-player-stats`,
          {
            goal_scorers: [],
            motm_player_id: validMotm,
            match_notes: matchNotes
          },
          { withCredentials: true }
        );
      }
      
      toast.success(`Game marked as completed! Score: ${homeInt}-${awayInt}`);
      setShowCompleteDialog(false);
      await fetchGames();
      
      // Update the details dialog with fresh data from API
      if (detailsGame && detailsGame.id === completeGame.id) {
        const gameResponse = await axios.get(`${API}/games/${completeGame.id}`, { withCredentials: true });
        setDetailsGame(gameResponse.data);
      }
    } catch (error) {
      console.error("Error marking game completed:", error);
      toast.error(error.response?.data?.detail || "Failed to mark game as completed");
    } finally {
      setCompleting(false);
    }
  };

  // Open Edit Goal Scorers dialog for a completed game
  const openEditScorersDialog = (game) => {
    setEditScorersGame(game);
    setScorerQuery("");

    // EVERYONE on the teamsheet is a candidate, not just signup_type
    // 'playing'. A Social/F&B sign-up who was put in the squad, and a call-up,
    // both scored plenty of goals and used to be missing from this list.
    const candidates = (game.signups || []).map((signup) => ({
      user_id: signup.is_guest ? signup.guest_id : signup.user_id,
      user_name: signup.is_guest ? signup.name : (signup.user_name || signup.name),
      isGuest: !!signup.is_guest,
      signupType: signup.signup_type,
      fromSquad: true,
    })).filter(c => (c.user_name || "").trim());
    candidates.sort((a, b) => (a.user_name || "").localeCompare(b.user_name || ""));
    setScorerCandidates(candidates);

    // Only the people actually credited get a row. Listing the whole squad
    // meant scrolling a wall of zeroes to find one name.
    const nameKey = (n) => (n || "").trim().toLowerCase();
    const rows = (game.goal_scorers || []).map((s) => {
      const known = candidates.find(c => (s.user_id && c.user_id === s.user_id)
        || (nameKey(s.user_name) && nameKey(c.user_name) === nameKey(s.user_name)));
      return {
        user_id: s.user_id || known?.user_id || null,
        user_name: s.user_name || known?.user_name || "",
        goals: s.goals || 0,
        assists: s.assists || 0,
        isGuest: s.isGuest ?? known?.isGuest ?? false,
        fromSquad: !!known,
      };
    });
    setEditScorersData(rows);
    setShowEditScorersDialog(true);
  };

  // Picked from the type-ahead: they scored at least one, that is why you picked them
  const addScorerFromSquad = (candidate) => {
    setScorerQuery("");
    setScorerPickerOpen(false);
    setEditScorersData((rows) => {
      const already = rows.findIndex(r => candidate.user_id
        ? r.user_id === candidate.user_id
        : (r.user_name || "").trim().toLowerCase() === (candidate.user_name || "").trim().toLowerCase());
      if (already >= 0) {
        const copy = [...rows];
        copy[already] = { ...copy[already], goals: (copy[already].goals || 0) + 1 };
        return copy;
      }
      return [...rows, { ...candidate, goals: 1, assists: 0 }];
    });
  };

  // Someone who never signed up but played and scored. A typed name that turns
  // out to be on the teamsheet, or already credited, must not become a second
  // row for the same person.
  const addManualScorer = (name = "") => {
    const key = name.trim().toLowerCase();
    if (key) {
      const credited = editScorersData.findIndex(
        r => (r.user_name || "").trim().toLowerCase() === key);
      if (credited >= 0) {
        setScorerQuery("");
        setScorerPickerOpen(false);
        setEditScorersData((rows) => rows.map((r, i) =>
          i === credited ? { ...r, goals: (r.goals || 0) + 1 } : r));
        return;
      }
      const onSheet = scorerCandidates.find(
        c => (c.user_name || "").trim().toLowerCase() === key);
      if (onSheet) {
        addScorerFromSquad(onSheet);
        return;
      }
    }
    setScorerQuery("");
    setScorerPickerOpen(false);
    setEditScorersData((rows) => [...rows, {
      user_id: null, user_name: name, goals: name ? 1 : 0, assists: 0,
      isGuest: true, fromSquad: false, isNew: true,
    }]);
  };

  const removeScorerRow = (index) => {
    setEditScorersData((rows) => rows.filter((_, i) => i !== index));
  };

  // Teamsheet minus anyone already credited, filtered by what is being typed
  const scorerSuggestions = (() => {
    const taken = new Set(editScorersData.flatMap(
      r => [r.user_id, (r.user_name || "").trim().toLowerCase()].filter(Boolean)));
    const q = scorerQuery.trim().toLowerCase();
    return scorerCandidates
      .filter(c => !taken.has(c.user_id)
        && !taken.has((c.user_name || "").trim().toLowerCase()))
      .filter(c => !q || (c.user_name || "").toLowerCase().includes(q))
      .slice(0, 8);
  })();

  // The typed text pointing at someone who is already on the scoresheet —
  // either their whole name, or enough of it to mean only them.
  const creditedMatchIndex = (() => {
    const q = scorerQuery.trim().toLowerCase();
    if (!q) return -1;
    const exact = editScorersData.findIndex(
      r => (r.user_name || "").trim().toLowerCase() === q);
    if (exact >= 0) return exact;
    const partial = editScorersData
      .map((r, i) => ({ i, name: (r.user_name || "").trim().toLowerCase() }))
      .filter(r => r.name.includes(q));
    return partial.length === 1 ? partial[0].i : -1;
  })();

  const bumpCreditedGoal = (index) => {
    setScorerQuery("");
    setScorerPickerOpen(false);
    setEditScorersData((rows) => rows.map((r, i) =>
      i === index ? { ...r, goals: (r.goals || 0) + 1 } : r));
  };


  // Save updated goal scorers
  const saveEditedScorers = async () => {
    if (!editScorersGame) return;

    const scorersToSave = editScorersData
      .filter(s => s.goals > 0 || s.assists > 0)
      .map(s => ({
        user_id: s.user_id || null,
        user_name: (s.user_name || "").trim(),
        goals: s.goals || 0,
        assists: s.assists || 0,
        isGuest: !!s.isGuest,
      }));

    const nameless = scorersToSave.find(s => !s.user_name);
    if (nameless) {
      toast.error("Put a name against every goal and assist before saving");
      return;
    }

    // A typed name that matches anyone on the teamsheet is that player: credit
    // their stats instead of saving an unattached guest with the same name.
    const sheetByName = {};
    scorerCandidates.filter(c => c.user_id).forEach(c => {
      sheetByName[(c.user_name || "").trim().toLowerCase()] = c;
    });
    editScorersData.filter(s => s.fromSquad && s.user_id).forEach(s => {
      sheetByName[(s.user_name || "").trim().toLowerCase()] = s;
    });
    scorersToSave.forEach(s => {
      if (!s.user_id) {
        const known = sheetByName[s.user_name.toLowerCase()];
        if (known) { s.user_id = known.user_id; s.isGuest = false; }
      }
    });

    // Two lines for one person (a pick plus a typed name) must reach the server
    // as one, or the stats are counted twice.
    const merged = [];
    scorersToSave.forEach(s => {
      const key = s.user_id || s.user_name.toLowerCase();
      const found = merged.find(m => (m.user_id || m.user_name.toLowerCase()) === key);
      if (found) {
        found.goals += s.goals;
        found.assists += s.assists;
      } else {
        merged.push(s);
      }
    });

    setSavingScorers(true);
    try {
      await axios.put(
        `${API}/games/${editScorersGame.id}/goal-scorers`,
        { goal_scorers: merged },
        { withCredentials: true }
      );
      
      toast.success("Goal scorers updated! Player stats have been adjusted.");
      setShowEditScorersDialog(false);
      await fetchGames();
      
      // Refresh details if open
      if (detailsGame && detailsGame.id === editScorersGame.id) {
        const gameResponse = await axios.get(`${API}/games/${editScorersGame.id}`, { withCredentials: true });
        setDetailsGame(gameResponse.data);
      }
    } catch (error) {
      console.error("Error updating goal scorers:", error);
      toast.error(error.response?.data?.detail || "Failed to update goal scorers");
    } finally {
      setSavingScorers(false);
    }
  };

  // Update a scorer's goals/assists in edit mode
  const updateEditScorer = (index, field, value) => {
    const updated = [...editScorersData];
    updated[index] = {
      ...updated[index],
      [field]: field === 'user_name' ? value : Math.max(0, parseInt(value) || 0),
    };
    setEditScorersData(updated);
  };

  const archiveGame = async (gameId) => {
    if (!window.confirm("Archive this game? This will close signups and preserve all records (match report, charge sheet). This action cannot be undone.")) {
      return;
    }

    try {
      await axios.post(`${API}/games/${gameId}/archive`, {}, {
        withCredentials: true
      });
      toast.success("Game archived successfully");
      await fetchGames();
      
      // Update the details dialog with fresh data from API
      if (detailsGame && detailsGame.id === gameId) {
        const gameResponse = await axios.get(`${API}/games/${gameId}`, { withCredentials: true });
        setDetailsGame(gameResponse.data);
      }
    } catch (error) {
      console.error("Error archiving game:", error);
      toast.error(error.response?.data?.detail || "Failed to archive game");
    }
  };

  // ============================================================================
  // ATTENDANCE FUNCTIONS
  // ============================================================================
  
  const openAttendanceDialog = async (game) => {
    try {
      const response = await axios.get(`${API}/games/${game.id}/attendance`, { withCredentials: true });
      setAttendanceGame(game);
      setAttendanceList(response.data.attendance || []);
      setAddPlayerName("");
      setAddPlayerMembership("");
      setShowAttendanceDialog(true);
    } catch (error) {
      console.error("Error fetching attendance:", error);
      toast.error("Failed to load attendance");
    }
  };

  const toggleAttendanceStatus = (index) => {
    const updated = [...attendanceList];
    const current = updated[index].status;
    // Cycle: present -> absent -> present
    updated[index].status = current === "present" ? "absent" : "present";
    setAttendanceList(updated);
  };

  const addLatePlayer = () => {
    if (!addPlayerName.trim()) {
      toast.error("Please enter player name");
      return;
    }
    
    const newPlayer = {
      user_id: `late_${Date.now()}`,
      user_name: addPlayerName.trim(),
      status: "late_addition",
      membership_number: addPlayerMembership.trim() || null,
      signup_type: "playing"
    };
    
    setAttendanceList([...attendanceList, newPlayer]);
    setAddPlayerName("");
    setAddPlayerMembership("");
    toast.success(`Added ${addPlayerName} as late addition`);
  };

  const saveAttendance = async () => {
    setSavingAttendance(true);
    try {
      const response = await axios.post(
        `${API}/games/${attendanceGame.id}/attendance`,
        { attendance: attendanceList },
        { withCredentials: true }
      );
      toast.success(response.data.message);
      setShowAttendanceDialog(false);
      
      // Refresh game data
      await fetchGames();
    } catch (error) {
      console.error("Error saving attendance:", error);
      toast.error(error.response?.data?.detail || "Failed to save attendance");
    } finally {
      setSavingAttendance(false);
    }
  };

  const submitMatchReport = async (asDraft = false) => {
    if (!reportTitle || !matchReport || scoreHome === "" || scoreAway === "") {
      toast.error("Please fill all fields");
      return;
    }

    try {
      const payload = {
        game_id: selectedGame.id,
        title: reportTitle,
        content: matchReport,
        score_home: parseInt(scoreHome),
        score_away: parseInt(scoreAway),
        status: asDraft ? "draft" : "published",
        author_name: reportAuthor || null  // Optional author name
      };

      // Check if editing existing draft
      if (selectedGame.match_report_id) {
        // Update existing draft or correct a published report
        await axios.put(`${API}/match-reports/${selectedGame.match_report_id}`, payload, { withCredentials: true });
        toast.success(editingPublishedReport ? "Correction saved"
          : asDraft ? "Draft updated! 💾" : "Report published! 🎉");
      } else {
        // Create new report
        await axios.post(`${API}/match-reports`, payload, { withCredentials: true });
        toast.success(asDraft ? "Draft saved! 💾" : "Report published! 🎉");
      }
      
      setShowReportDialog(false);
      setReportTitle("");
      setMatchReport("");
      setReportAuthor("");
      setScoreHome("");
      setScoreAway("");
      setSelectedGame(null);
      fetchGames();
    } catch (error) {
      console.error("Error submitting report:", error);
      toast.error(error.response?.data?.detail || "Failed to submit report");
    }
  };

  const openReportDialog = async (game) => {
    try {
      const response = await axios.get(`${API}/match-reports/by-game/${game.id}`, { withCredentials: true });
      // A published report can now be corrected — a wrong score or a missing
      // name is worse than an edit. Ratings and comments are kept.
      setEditingPublishedReport(response.data.status === "published");
      setSelectedGame({ ...game, match_report_id: response.data.id });
      setReportTitle(response.data.title || "");
      setMatchReport(response.data.content || "");
      setReportAuthor(response.data.author_name || "");
      setScoreHome(response.data.score_home?.toString() || "");
      setScoreAway(response.data.score_away?.toString() || "");
    } catch (error) {
      // No report exists for this game. Clear any stale match_report_id on the
      // game (some carry a placeholder), or the save goes to a report that
      // isn't there and the game can never get one.
      setEditingPublishedReport(false);
      setSelectedGame({ ...game, match_report_id: null });
      setReportTitle("");
      setMatchReport("");
      setReportAuthor("");
      setScoreHome(game.score_home?.toString() || "");
      setScoreAway(game.score_away?.toString() || "");
    }
    
    setShowReportDialog(true);
  };

  const addGoalScorer = () => {
    setGoalScorers([...goalScorers, { user_id: "", user_name: "", assists: [] }]);
  };
  
  const removeGoalScorer = (index) => {
    setGoalScorers(goalScorers.filter((_, i) => i !== index));
  };
  
  const updateGoalScorer = (index, oderId, userName) => {
    const updated = [...goalScorers];
    updated[index].user_id = oderId;
    updated[index].user_name = userName;
    setGoalScorers(updated);
  };
  
  const addAssist = (scorerIndex) => {
    const updated = [...goalScorers];
    updated[scorerIndex].assists.push({ user_id: "", user_name: "" });
    setGoalScorers(updated);
  };
  
  const removeAssist = (scorerIndex, assistIndex) => {
    const updated = [...goalScorers];
    updated[scorerIndex].assists = updated[scorerIndex].assists.filter((_, i) => i !== assistIndex);
    setGoalScorers(updated);
  };
  
  const updateAssist = (scorerIndex, assistIndex, playerId, playerName) => {
    const updated = [...goalScorers];
    updated[scorerIndex].assists[assistIndex] = { user_id: playerId, user_name: playerName };
    setGoalScorers(updated);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a] overflow-x-hidden">
      {/* Header */}
      <header className="bg-slate-950 border-b border-white/5">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" className="text-slate-400 hover:text-white hover:bg-slate-900/50/5 px-2">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <h1 className="text-xl md:text-2xl font-bold text-yellow-500 tracking-wide font-display">
              SCC Football
            </h1>
          </div>
          <ThemeToggle />
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-6 md:py-12 max-w-full overflow-x-hidden">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6 md:mb-8">
          <div className="min-w-0">
            <h2 className="text-2xl md:text-4xl font-bold text-white font-display" data-testid="page-title">Game Schedules</h2>
            {isAcademyParent && !isSectionMember && (
              <p className="text-xs md:text-sm text-slate-400 mt-2">
                Showing games for your children&apos;s teams
              </p>
            )}
            {user?.roles?.includes("academy_coach") && !isSectionMember && (
              <p className="text-xs md:text-sm text-slate-400 mt-2">
                Showing games for teams you coach
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {/* View Tabs */}
            <div className="flex bg-slate-800/50 rounded-lg p-1 border border-white/5 mr-2">
              <button
                onClick={() => setViewTab("upcoming")}
                className={`px-3 py-1.5 text-xs md:text-sm font-medium rounded-md transition-colors ${
                  viewTab === "upcoming"
                    ? "bg-yellow-500/100 text-black"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                Upcoming
              </button>
              <button
                onClick={() => setViewTab("archived")}
                className={`px-3 py-1.5 text-xs md:text-sm font-medium rounded-md transition-colors ${
                  viewTab === "archived"
                    ? "bg-yellow-500/100 text-black"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                Archived
              </button>
            </div>
            <Link to="/game-calendar">
              <Button variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white text-xs md:text-sm">
                <CalendarIcon className="w-4 h-4 md:w-5 md:h-5 mr-1 md:mr-2" />
                <span className="hidden sm:inline">Calendar</span>
              </Button>
            </Link>
            {isCaptain && (
              <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-yellow-500/100 hover:bg-yellow-600 text-black font-semibold" data-testid="create-game-button">
                    <Plus className="w-5 h-5 mr-2" />
                    Schedule Game
                  </Button>
                </DialogTrigger>
              <DialogContent data-testid="create-game-dialog">
                <DialogHeader>
                  <DialogTitle>Schedule New Game</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Team</label>
                    <Select value={teamId} onValueChange={setTeamId}>
                      <SelectTrigger data-testid="team-select">
                        <SelectValue placeholder="Select team" />
                      </SelectTrigger>
                      <SelectContent>
                        {teams.map((team) => (
                          <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Date &amp; Kick-off time *</label>
                    <Input
                      type="datetime-local"
                      required
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      data-testid="game-date-input"
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      Players are told the kick-off time in their game message, so it can&apos;t be left blank.
                    </p>
                  </div>
                  <div className="relative">
                    <label className="text-sm font-medium mb-2 block">Location</label>
                    <Input
                      placeholder="Start typing venue name..."
                      value={venueSearch || location}
                      onChange={(e) => {
                        setVenueSearch(e.target.value);
                        setLocation(e.target.value);
                        setSelectedVenueArea("");  // Clear area when typing custom
                      }}
                      onFocus={() => venueSearch && setShowVenueSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowVenueSuggestions(false), 200)}
                      data-testid="game-location-input"
                    />
                    
                    {/* Venue Autocomplete Dropdown */}
                    {showVenueSuggestions && venueSuggestions.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-lg max-h-60 overflow-auto">
                        {venueSuggestions.map((venue) => (
                          <button
                            key={venue.id}
                            type="button"
                            className="w-full px-3 py-2 text-left hover:bg-slate-700 flex items-center justify-between"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              selectVenue(venue);
                            }}
                          >
                            <div>
                              <p className="text-white font-medium">{venue.name}</p>
                              <p className="text-xs text-slate-400">{venue.address || venue.nea_area}</p>
                            </div>
                            <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">
                              {venue.nea_area}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {/* Selected Area Indicator */}
                    {selectedVenueArea && (
                      <div className="flex items-center justify-between mt-2 p-2 bg-green-500/10 border border-green-500/30 rounded text-sm">
                        <span className="text-green-400">📍 Weather area: {selectedVenueArea}</span>
                        <button
                          type="button"
                          onClick={() => setShowAreaOverride(!showAreaOverride)}
                          className="text-xs text-slate-400 hover:text-white"
                        >
                          {showAreaOverride ? "Hide" : "Change"}
                        </button>
                      </div>
                    )}
                    
                    {/* Area Override */}
                    {(showAreaOverride || (!selectedVenueArea && location)) && (
                      <div className="mt-2">
                        <label className="text-xs text-slate-400 mb-1 block">
                          Weather forecast area {!selectedVenueArea && "(select for weather alerts)"}
                        </label>
                        <select
                          value={selectedVenueArea}
                          onChange={(e) => setSelectedVenueArea(e.target.value)}
                          className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm"
                        >
                          <option value="">-- Select area --</option>
                          {neaAreas.map((area) => (
                            <option key={area} value={area}>{area}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Opponent</label>
                    <Input
                      placeholder="Opponent team name"
                      value={opponent}
                      onChange={(e) => setOpponent(e.target.value)}
                      data-testid="game-opponent-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Squad Limit (Optional)</label>
                    <Input
                      type="number"
                      placeholder="e.g., 18 for 11-a-side, 12 for 7-a-side"
                      value={squadLimit}
                      onChange={(e) => setSquadLimit(e.target.value)}
                      data-testid="squad-limit-input"
                    />
                    <p className="text-xs text-slate-400 mt-1">Leave empty for no limit</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Standby Promotion</label>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="standbyPromotion"
                          value="manual"
                          checked={standbyPromotion === "manual"}
                          onChange={(e) => setStandbyPromotion(e.target.value)}
                          className="w-4 h-4"
                        />
                        <span>Manual (Captain chooses)</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="standbyPromotion"
                          value="auto"
                          checked={standbyPromotion === "auto"}
                          onChange={(e) => setStandbyPromotion(e.target.value)}
                          className="w-4 h-4"
                        />
                        <span>Auto (First standby)</span>
                      </label>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">How to handle standby when someone withdraws</p>
                  </div>
                  
                  {/* Logistics Request Section */}
                  <div className="border-t border-slate-700 pt-4">
                    <LogisticsForm 
                      teamId={teamId}
                      user={user}
                      onChange={setLogistics}
                    />
                  </div>
                  
                  <Button
                    onClick={createGame}
                    className="w-full bg-yellow-500/100 hover:bg-yellow-600 text-black font-semibold"
                    data-testid="submit-game-button"
                  >
                    {logistics ? "Create Game & Send Logistics Request" : "Create Game"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            )}
          </div>
        </div>

        {filteredGames.length === 0 ? (
          <Card className="p-8 text-center bg-slate-900/50 border border-white/5" data-testid="no-games-message">
            <p className="text-slate-400">
              {viewTab === "upcoming" 
                ? "No upcoming games scheduled. Check back soon!" 
                : "No archived games yet."}
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredGames.map((game, index) => {
              const team = teams.find(t => t.id === game.team_id);
              const signups = game.signups || [];
              const standby = game.standby || [];
              const isSignedUp = signups.some(s => s.user_id === user.id);
              const isStandby = standby.some(s => s.user_id === user.id);
              const squadLimit = game.squad_limit;
              const isFull = squadLimit && signups.length >= squadLimit;
              // So a captain sees the whole picture, not just the yeses.
              const socialCount = signups.filter(s => s.signup_type === 'social_fb_only').length;
              const playingCount = signups.length - socialCount;
              const declinedCount = (game.declined || []).length;
              const squadSize = team?.player_ids?.length || team?.members?.length || 0;
              const noReplyCount = Math.max(
                0, squadSize - signups.length - standby.length - declinedCount);
              // Some of that silence is people we never reached.
              const teamReach = reach[game.team_id] || {};
              const unreachableLabel = [
                teamReach.muted ? `${teamReach.muted} muted this team` : null,
                teamReach.opted_out ? `${teamReach.opted_out} opted out of WhatsApp` : null,
                teamReach.no_mobile ? `${teamReach.no_mobile} with no mobile number` : null,
              ].filter(Boolean).join(", ");
              
              // Check if we need to show a separator before this game
              const showCompletedSeparator = viewTab === "upcoming" && 
                isGameComplete(game) && 
                (index === 0 || !isGameComplete(filteredGames[index - 1]));
              
              return (
                <div key={game.id}>
                  {/* Separator between upcoming and completed/past games */}
                  {showCompletedSeparator && (
                    <div className="flex items-center gap-4 py-4 mb-4" data-testid="completed-separator">
                      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-600 to-transparent"></div>
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-3 py-1 bg-slate-800/50 rounded-full border border-slate-700">
                        Completed / Past Games
                      </span>
                      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-600 to-transparent"></div>
                    </div>
                  )}
                  <Card
                    className="p-4 md:p-6 bg-slate-900/50 border border-white/5 rounded-xl hover:border-yellow-500/30 transition-all"
                    data-testid={`game-card-${game.id}`}
                  >
                    {/* Status badges - mobile friendly row at top */}
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {game.status === 'cancelled' && (
                        <span className="text-xs bg-red-600 text-white px-2 py-0.5 rounded font-semibold">
                          CANCELLED
                        </span>
                      )}
                      {game.status === 'archived' && (
                        <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded font-semibold">
                          ARCHIVED
                        </span>
                      )}
                      {game.signups_closed && !game.status && (
                        <span className="text-xs bg-orange-600 text-white px-2 py-0.5 rounded font-semibold">
                          SIGNUPS CLOSED
                        </span>
                      )}
                      {game.match_report_id && (
                        <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded font-semibold">
                          ✓ REPORT
                        </span>
                      )}
                    {isFull && !game.status && (
                      <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded font-medium">
                        FULL
                      </span>
                    )}
                  </div>
                  
                  <div className="flex flex-col lg:flex-row justify-between items-start gap-4">
                    <div className="flex-1 min-w-0 w-full">
                      {/* Title */}
                      <div className="flex items-start gap-2 mb-3">
                        <Trophy className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                        <h3 className="text-base md:text-xl font-bold text-white break-words" data-testid={`game-title-${game.id}`}>
                          {team?.name || "Team"} vs {game.opponent}
                        </h3>
                      </div>
                      
                      {/* Game details */}
                      <div className="space-y-1.5 text-slate-400 text-sm">
                        <div className="flex items-center gap-2">
                          <CalendarIcon className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate" data-testid={`game-date-${game.id}`}>{new Date(game.date).toLocaleString()}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate" data-testid={`game-location-${game.id}`}>{game.location}</span>
                        </div>
                        <div className="flex items-center gap-3 flex-wrap text-xs md:text-sm">
                          <p className="text-green-400" data-testid={`game-signups-${game.id}`}>
                            <span className="font-semibold">{playingCount}</span>
                            {squadLimit ? ` / ${squadLimit}` : ''} playing
                          </p>
                          {standby.length > 0 && (
                            <p className="text-orange-600">
                              <span className="font-semibold">{standby.length}</span> standby
                            </p>
                          )}
                          {socialCount > 0 && (
                            <p className="text-sky-400" data-testid={`game-social-${game.id}`}>
                              <span className="font-semibold">{socialCount}</span> F&B
                            </p>
                          )}
                          <p className="text-red-400" data-testid={`game-declined-${game.id}`}>
                            <span className="font-semibold">{declinedCount}</span> unavailable
                          </p>
                          {squadSize > 0 && (
                            <p className="text-slate-500" data-testid={`game-noreply-${game.id}`}>
                              <span className="font-semibold">{noReplyCount}</span> no reply
                            </p>
                          )}
                          {teamReach.unreachable > 0 && (
                            <p className="text-slate-500" title={unreachableLabel}
                              data-testid={`game-unreachable-${game.id}`}>
                              <span className="font-semibold">{teamReach.unreachable}</span> not messaged
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {/* Score and Match Details - Show for completed/archived games OR if goal_scorers exist */}
                      {(game.status === 'completed' || game.status === 'archived' || game.score_home !== undefined || (game.goal_scorers && game.goal_scorers.length > 0)) && (
                        <div className="mt-4 p-4 bg-slate-800/50 rounded-lg border border-white/5" data-testid={`game-details-${game.id}`}>
                          {/* Score */}
                          {(game.score_home != null && game.score_away != null) && (
                            <div className="flex items-center gap-3 mb-3">
                              <span className="text-2xl font-bold text-white">
                                {game.score_home} - {game.score_away}
                              </span>
                              <span className={`text-sm px-2 py-0.5 rounded ${
                                game.score_home > game.score_away ? 'bg-green-500/20 text-green-400' :
                                game.score_home < game.score_away ? 'bg-red-500/20 text-red-400' :
                                'bg-yellow-500/20 text-yellow-400'
                              }`}>
                                {game.score_home > game.score_away ? 'WIN' : 
                                 game.score_home < game.score_away ? 'LOSS' : 'DRAW'}
                              </span>
                            </div>
                          )}
                          
                          {/* Goal Scorers */}
                          {game.goal_scorers && game.goal_scorers.length > 0 && (
                            <div className="mb-2">
                              <span className="text-xs text-slate-500 uppercase">Goal Scorers:</span>
                              <div className="flex flex-wrap gap-2 mt-1">
                                {game.goal_scorers.map((scorer, idx) => (
                                  <span key={idx} className="text-sm bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded">
                                    ⚽ {scorer.user_name} {scorer.goals > 1 ? `(${scorer.goals})` : ''}
                                    {scorer.assists > 0 && <span className="text-blue-400 ml-1">🅰️{scorer.assists}</span>}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* MOTM */}
                          {game.motm_player_name && (
                            <div className="mb-2">
                              <span className="text-xs text-slate-500 uppercase">Man of the Match:</span>
                              <span className="ml-2 text-sm bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded">
                                ⭐ {game.motm_player_name}
                              </span>
                            </div>
                          )}
                          
                          {/* Match Notes */}
                          {game.match_notes && (
                            <p className="text-sm text-slate-400 mt-2 italic">"{game.match_notes}"</p>
                          )}
                          
                          {/* Match Report Link */}
                          {game.match_report && (
                            <p className="text-sm text-slate-300 mt-2">{game.match_report}</p>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Action buttons - responsive grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:flex lg:flex-col gap-2 w-full lg:w-auto lg:min-w-[140px]">
                      {isSignedUp ? (
                        <Button
                          onClick={() => withdrawFromGame(game.id)}
                          variant="outline"
                          size="sm"
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10 text-xs md:text-sm w-full"
                          data-testid={`withdraw-button-${game.id}`}
                        >
                          Withdraw
                        </Button>
                      ) : isStandby ? (
                        <>
                          <Button
                            disabled
                            size="sm"
                            className="bg-orange-500/20 text-orange-400 text-xs md:text-sm w-full"
                            data-testid={`standby-button-${game.id}`}
                          >
                            On Standby
                          </Button>
                          <Button
                            onClick={() => withdrawFromGame(game.id)}
                            variant="outline"
                            size="sm"
                            className="border-red-500/50 text-red-400 hover:bg-red-500/10 text-xs w-full"
                            data-testid={`withdraw-standby-button-${game.id}`}
                          >
                            Withdraw
                          </Button>
                        </>
                      ) : game.declined?.some(d => d.user_id === user?.id) ? (
                        <Button
                          onClick={() => undoDecline(game.id)}
                          variant="outline"
                          size="sm"
                          className="border-slate-600 text-slate-400 hover:bg-slate-800 text-xs md:text-sm w-full"
                          data-testid={`undo-decline-button-${game.id}`}
                        >
                          Unavailable ✓
                        </Button>
                      ) : !game.signups_closed && game.status !== 'archived' && game.status !== 'cancelled' ? (
                        <>
                          <Button
                            onClick={() => openSignupDialog(game)}
                            size="sm"
                            className="bg-yellow-500/100 hover:bg-yellow-600 text-black font-semibold text-xs md:text-sm w-full"
                            data-testid={`signup-button-${game.id}`}
                          >
                            Sign Up
                          </Button>
                          <Button
                            onClick={() => declineGame(game.id)}
                            variant="outline"
                            size="sm"
                            className="border-slate-600 text-slate-400 hover:bg-slate-800 text-xs w-full"
                            data-testid={`decline-button-${game.id}`}
                          >
                            Not Available
                          </Button>
                        </>
                      ) : null}
                      
                      <Button
                        onClick={() => viewGameDetails(game)}
                        variant="outline"
                        size="sm"
                        className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10 text-xs md:text-sm w-full"
                        data-testid={`details-button-${game.id}`}
                      >
                        View Details
                      </Button>
                      
                      {/* Show management buttons only if user can manage THIS game's team */}
                      {canManageGameTeam(game.team_id) && (
                        <>
                          {/* Edit Game - Hide if signups closed, match report submitted or completed (unless convenor) */}
                          {(!game.signups_closed && !game.match_report_id && game.status !== 'completed' && game.status !== 'archived') || user.role === 'convenor' ? (
                            <Button
                              onClick={() => openEditDialog(game)}
                              variant="outline"
                              size="sm"
                              className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10 text-xs w-full"
                              data-testid={`edit-button-${game.id}`}
                              disabled={(game.signups_closed || game.match_report_id || game.status === 'completed') && user.role !== 'convenor'}
                            >
                              Edit
                            </Button>
                          ) : null}
                          
                          {/* Logistics Button - Allow anytime before game is archived/cancelled */}
                          {game.status !== 'cancelled' && game.status !== 'archived' && (
                            <Button
                              onClick={() => openLogisticsDialog(game)}
                              variant="outline"
                              size="sm"
                              className={`text-xs w-full ${
                                game.logistics_request?.id 
                                  ? 'border-green-500/50 text-green-400 hover:bg-green-500/10' 
                                  : 'border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10'
                              }`}
                              data-testid={`logistics-button-${game.id}`}
                            >
                              {game.logistics_request?.id ? '✓ Logistics' : '📋 Logistics'}
                            </Button>
                          )}
                          
                          {/* Cancel/Archive Button */}
                          {game.status !== 'cancelled' && game.status !== 'archived' && (
                            <>
                              {game.match_report_id || chargeSheetStatuses[game.id]?.exists ? (
                                <Button
                                  onClick={() => archiveGame(game.id)}
                                  variant="outline"
                                  size="sm"
                                  className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10 text-xs w-full"
                                  data-testid={`archive-button-${game.id}`}
                                >
                                  Archive
                                </Button>
                              ) : (
                                <Button
                                  onClick={() => cancelGame(game.id)}
                                  variant="outline"
                                  size="sm"
                                  className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10 text-xs w-full"
                                  data-testid={`cancel-button-${game.id}`}
                                >
                                  Cancel
                                </Button>
                              )}
                            </>
                          )}
                          
                          {/* Match Report / Mark Completed */}
                          {game.status !== 'completed' && game.status !== 'archived' ? (
                            <Button
                              onClick={() => markGameCompleted(game.id)}
                              variant="outline"
                              size="sm"
                              className="border-green-500/50 text-green-400 hover:bg-green-500/10 text-xs w-full"
                              data-testid={`complete-button-${game.id}`}
                            >
                              Complete
                            </Button>
                          ) : !game.match_report_id ? (
                            <Button
                              onClick={() => openReportDialog(game)}
                              variant="outline"
                              size="sm"
                              className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10 text-xs w-full"
                              data-testid={`report-button-${game.id}`}
                            >
                              Add Report
                            </Button>
                          ) : reportStatuses[game.id]?.status === 'draft' ? (
                            <Button
                              onClick={() => openReportDialog(game)}
                              variant="outline"
                              size="sm"
                              className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10 text-xs w-full"
                              data-testid={`report-button-${game.id}`}
                            >
                              Edit Draft
                            </Button>
                          ) : (
                            <Button
                              onClick={() => openReportDialog(game)}
                              variant="outline"
                              size="sm"
                              className="border-green-500/50 text-green-400 hover:bg-green-500/10 text-xs w-full"
                              data-testid={`report-button-${game.id}`}
                            >
                              Edit Report
                            </Button>
                          )}
                          
                          {/* Edit Goal Scorers - for completed/archived games */}
                          {(game.status === 'completed' || game.status === 'archived') && (
                            <Button
                              onClick={() => openEditScorersDialog(game)}
                              variant="outline"
                              size="sm"
                              className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10 text-xs w-full"
                              data-testid={`edit-scorers-button-${game.id}`}
                            >
                              <Trophy className="w-3 h-3 mr-1" />
                              {game.goal_scorers?.length > 0 ? "Edit Scorers" : "Add Scorers"}
                            </Button>
                          )}
                          
                          {/* Charge Sheet */}
                          <Button
                            onClick={() => {
                              setChargeSheetGame(game);
                              setShowChargeSheet(true);
                            }}
                            variant="outline"
                            size="sm"
                            className={`text-xs w-full ${
                              chargeSheetStatuses[game.id]?.exists
                                ? "border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                                : "border-green-500/50 text-green-400 hover:bg-green-500/10"
                            }`}
                            data-testid={`charge-sheet-button-${game.id}`}
                          >
                            <DollarSign className="w-3 h-3 mr-1" />
                            {chargeSheetStatuses[game.id]?.exists ? "Charges ✓" : "Charges"}
                          </Button>
                          
                          {/* Attendance */}
                          <Button
                            onClick={() => openAttendanceDialog(game)}
                            variant="outline"
                            size="sm"
                            className={`text-xs w-full ${
                              game.attendance_taken
                                ? "border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                                : "border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                            }`}
                            data-testid={`attendance-button-${game.id}`}
                          >
                            <Users className="w-3 h-3 mr-1" />
                            {game.attendance_taken ? "Attendance ✓" : "Attendance"}
                          </Button>
                          
                          {/* Delete - Only show if no charge sheet submitted */}
                          {!chargeSheetStatuses[game.id]?.exists && !game.match_report_id && (
                            <Button
                              onClick={() => deleteGame(game.id)}
                              variant="outline"
                              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                              data-testid={`delete-button-${game.id}`}
                            >
                              Delete
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </Card>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Match Report Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent data-testid="match-report-dialog">
          <DialogHeader>
            <DialogTitle>
              {selectedGame?.match_report_id ? "✏️ Edit Match Report" : "📝 Submit Match Report"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-400">
              {editingPublishedReport
                ? "This report is already live. Corrections save straight away and everyone's ratings and comments are kept."
                : selectedGame?.match_report_id
                  ? "Still a draft — only staff can see it. Publish it when you're happy for everyone to read it."
                  : "Your report will be published to all members and can be liked, rated & commented on!"}
            </p>
            <div>
              <label className="text-sm font-medium mb-2 block">Report Title</label>
              <Input
                placeholder="e.g., Thrilling Victory Against City Rivals"
                value={reportTitle}
                onChange={(e) => setReportTitle(e.target.value)}
                data-testid="report-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Home Score</label>
                <Input
                  type="number"
                  value={scoreHome}
                  onChange={(e) => setScoreHome(e.target.value)}
                  data-testid="score-home-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Away Score</label>
                <Input
                  type="number"
                  value={scoreAway}
                  onChange={(e) => setScoreAway(e.target.value)}
                  data-testid="score-away-input"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Match Report</label>
              <Textarea
                placeholder="Share the highlights, key moments, and player performances... Use @username to mention players!"
                value={matchReport}
                onChange={(e) => setMatchReport(e.target.value)}
                rows={8}
                data-testid="match-report-textarea"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Author Name (optional)</label>
              <Input
                placeholder="Leave blank to use your name"
                value={reportAuthor}
                onChange={(e) => setReportAuthor(e.target.value)}
                data-testid="report-author-input"
              />
              <p className="text-xs text-slate-400 mt-1">Who wrote this report? Leave blank if you wrote it.</p>
            </div>
            
            <div className="flex gap-3">
              {!editingPublishedReport && (
                <Button
                  onClick={() => submitMatchReport(true)}
                  variant="outline"
                  className="flex-1 border-slate-600"
                  data-testid="save-draft-button"
                >
                  💾 Save as Draft
                </Button>
              )}
              <Button
                onClick={() => submitMatchReport(false)}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold"
                data-testid="publish-report-button"
              >
                {editingPublishedReport ? "Save the correction" : "📢 Publish Now"}
              </Button>
            </div>

            {editingPublishedReport && (
              <p className="text-xs text-slate-400 text-center">
                It stays published, and shows when it was last corrected.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Signup Dialog */}
      <Dialog open={showSignupDialog} onOpenChange={setShowSignupDialog}>
        <DialogContent data-testid="signup-dialog">
          <DialogHeader>
            <DialogTitle>Sign Up for Game</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-slate-400 font-medium">
              {signupGame && `${teams.find(t => t.id === signupGame.team_id)?.name || 'Team'} vs ${signupGame.opponent}`}
            </p>
            
            {/* Calendar Conflict Warning */}
            {checkingConflicts && (
              <div className="bg-slate-800/50 p-3 rounded border border-slate-700 flex items-center gap-2">
                <RefreshCw className="w-4 h-4 animate-spin text-slate-400" />
                <span className="text-sm text-slate-400">Checking calendar...</span>
              </div>
            )}
            
            {calendarConflicts?.has_conflicts && (
              <div className={`p-3 rounded border ${calendarConflicts.will_block_signup 
                ? 'bg-red-500/20 border-red-500/50' 
                : 'bg-yellow-500/20 border-yellow-500/50'}`}>
                <div className="flex items-start gap-2">
                  <AlertTriangle className={`w-5 h-5 mt-0.5 ${calendarConflicts.will_block_signup ? 'text-red-400' : 'text-yellow-400'}`} />
                  <div>
                    <p className={`font-medium text-sm ${calendarConflicts.will_block_signup ? 'text-red-300' : 'text-yellow-300'}`}>
                      {calendarConflicts.will_block_signup ? 'Calendar Conflict - Cannot Sign Up' : 'Calendar Conflict Warning'}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      You have conflicting events during this game:
                    </p>
                    <ul className="text-xs text-slate-300 mt-1 space-y-0.5">
                      {calendarConflicts.conflicts.slice(0, 3).map((c, i) => (
                        <li key={i}>• {c.title}</li>
                      ))}
                    </ul>
                    {calendarConflicts.will_block_signup && (
                      <p className="text-xs text-slate-400 mt-2">
                        Disable "Auto-exclude when busy" in <a href="/outlook-calendar" className="text-blue-400 underline">Calendar Settings</a> to sign up anyway.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            <div className="bg-slate-800/50 p-4 rounded border">
              <label className="text-sm font-medium mb-3 block">How will you participate?</label>
              <div className="space-y-2">
                <label className="flex items-center gap-3 cursor-pointer p-2 hover:bg-slate-900/50 rounded">
                  <input
                    type="radio"
                    name="signupType"
                    value="playing"
                    checked={signupType === "playing"}
                    onChange={(e) => setSignupType(e.target.value)}
                    className="w-4 h-4"
                  />
                  <div>
                    <p className="font-medium text-white">Playing</p>
                    <p className="text-xs text-slate-400">I will play in the game</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 cursor-pointer p-2 hover:bg-slate-900/50 rounded">
                  <input
                    type="radio"
                    name="signupType"
                    value="social_fb_only"
                    checked={signupType === "social_fb_only"}
                    onChange={(e) => setSignupType(e.target.value)}
                    className="w-4 h-4"
                  />
                  <div>
                    <p className="font-medium text-white">Social/F&B Only</p>
                    <p className="text-xs text-slate-400">Coming for support and drinks</p>
                  </div>
                </label>
              </div>
            </div>
            <Button
              onClick={signupForGame}
              className="w-full bg-yellow-500/100 hover:bg-yellow-600 text-black font-semibold"
              data-testid="confirm-signup-button"
              disabled={calendarConflicts?.will_block_signup}
            >
              {calendarConflicts?.will_block_signup ? 'Cannot Sign Up (Calendar Conflict)' : 'Confirm Signup'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Game Details Dialog */}
      <Dialog open={showGameDetails} onOpenChange={setShowGameDetails}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="game-details-dialog">
          <DialogHeader>
            <DialogTitle>Game Details</DialogTitle>
          </DialogHeader>
          {detailsGame && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-semibold text-slate-400">Team:</p>
                  <p className="text-white">{teams.find(t => t.id === detailsGame.team_id)?.name || 'N/A'}</p>
                </div>
                <div>
                  <p className="font-semibold text-slate-400">Opponent:</p>
                  <p className="text-white">{detailsGame.opponent}</p>
                </div>
                <div>
                  <p className="font-semibold text-slate-400">Date:</p>
                  <p className="text-white">{new Date(detailsGame.date).toLocaleString()}</p>
                </div>
                <div>
                  <p className="font-semibold text-slate-400">Location:</p>
                  <p className="text-white">{detailsGame.location}</p>
                </div>
                {detailsGame.squad_limit && (
                  <div>
                    <p className="font-semibold text-slate-400">Squad Limit:</p>
                    <p className="text-white">{detailsGame.squad_limit} players</p>
                  </div>
                )}
              </div>

              {/* Squad Status Summary */}
              {canManageGameTeam(detailsGame.team_id)
                && (reach[detailsGame.team_id]?.unreachable > 0) && (
                <div className="bg-slate-800/60 border border-white/10 rounded p-4"
                  data-testid="game-reach-breakdown">
                  <p className="font-semibold text-slate-200 text-sm mb-1">
                    {reach[detailsGame.team_id].unreachable} of your squad never got the message
                  </p>
                  <ul className="text-xs text-slate-400 space-y-0.5">
                    {reach[detailsGame.team_id].muted > 0 && (
                      <li data-testid="reach-muted">
                        {reach[detailsGame.team_id].muted} muted this team in the portal
                      </li>
                    )}
                    {reach[detailsGame.team_id].opted_out > 0 && (
                      <li data-testid="reach-optedout">
                        {reach[detailsGame.team_id].opted_out} replied STOP on WhatsApp
                      </li>
                    )}
                    {reach[detailsGame.team_id].no_mobile > 0 && (
                      <li data-testid="reach-nomobile">
                        {reach[detailsGame.team_id].no_mobile} have no mobile number on file
                      </li>
                    )}
                  </ul>
                  <p className="text-xs text-slate-500 mt-2">
                    Count them as quiet, not unwilling — chase them another way.
                  </p>
                </div>
              )}

              {detailsGame.squad_limit && (
                <div className="bg-slate-800/60 border-2 border-blue-500/30 rounded p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold text-blue-300">Squad Limit:</span>
                    <span className="text-xl font-bold text-blue-300">
                      {(detailsGame.signups || []).length} / {detailsGame.squad_limit}
                    </span>
                  </div>
                  <div className="w-full bg-slate-700 rounded-full h-3">
                    <div 
                      className="bg-blue-500/100 h-3 rounded-full transition-all"
                      style={{ 
                        width: `${Math.min(((detailsGame.signups || []).length / detailsGame.squad_limit) * 100, 100)}%` 
                      }}
                    />
                  </div>
                  <div className="mt-2 space-y-1">
                    {(detailsGame.signups || []).length >= detailsGame.squad_limit && (
                      <p className="text-xs text-red-400 font-semibold">
                        ⚠️ Squad is FULL - Additional signups go to standby
                      </p>
                    )}
                    {(detailsGame.standby || []).length > 0 && (
                      <p className="text-xs text-orange-400 font-semibold">
                        📋 {(detailsGame.standby || []).length} player(s) currently on standby
                      </p>
                    )}
                    {detailsGame.standby_promotion === "auto" && (detailsGame.standby || []).length > 0 && (
                      <p className="text-xs text-green-400 font-semibold">
                        ⚡ Auto-promote enabled - standby players will be promoted automatically
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Pending Signups - Captain Approval Required */}
              {canManageGameTeam(detailsGame.team_id) && (detailsGame.pending_signups || []).length > 0 && (
                <div className="bg-slate-800/60 border-2 border-yellow-500/30 rounded p-4">
                  <h3 className="font-bold text-yellow-400 mb-3">
                    ⏳ Pending Approval ({(detailsGame.pending_signups || []).length})
                  </h3>
                  <p className="text-xs text-yellow-300 mb-3">
                    These players are waiting for your approval to join the game
                  </p>
                  <div className="space-y-2">
                    {(detailsGame.pending_signups || []).map((signup, index) => (
                      <div key={index} className="flex justify-between items-center p-2 bg-slate-900/50 border border-yellow-500/30 rounded">
                        <div className="flex items-center gap-2">
                          <span className="text-slate-100 font-medium">{signup.user_name}</span>
                          <span className={`text-xs px-2 py-1 rounded ${
                            signup.signup_type === 'playing' 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {signup.signup_type === 'playing' ? 'Playing' : 'Social/F&B'}
                          </span>
                          <span className="text-xs bg-yellow-500/100 text-white px-2 py-0.5 rounded font-semibold">
                            PENDING
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handlePendingSignup(detailsGame.id, signup.user_id, "approve_main")}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handlePendingSignup(detailsGame.id, signup.user_id, "reject")}
                            className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                          >
                            Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Main Squad Players */}
              <div>
                <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
                  <h3 className="font-bold text-white whitespace-nowrap">
                    Main Squad ({(detailsGame.signups || []).length}{detailsGame.squad_limit ? ` / ${detailsGame.squad_limit}` : ''})
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {/* WhatsApp Share Buttons */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => shareSquadToWhatsApp(detailsGame)}
                      className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                      title="Share squad to WhatsApp"
                    >
                      <MessageCircle className="w-4 h-4 mr-1" />
                      WhatsApp
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copySquadToClipboard(detailsGame)}
                      className="border-slate-500/50 text-slate-300 hover:bg-slate-500/10"
                      title="Copy squad list"
                    >
                      <Share2 className="w-4 h-4 mr-1" />
                      Copy
                    </Button>
                    {canManageGameTeam(detailsGame.team_id) && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={announcing === `ask-${detailsGame.id}`}
                        onClick={() => askSquadNow(detailsGame)}
                        className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                        title="Ask the whole team YES or NO right now, whatever the schedule says"
                        data-testid="ask-squad-now-btn"
                      >
                        <MessageCircle className="w-4 h-4 mr-1" />
                        {announcing === `ask-${detailsGame.id}` ? "Sending…" : "Ask squad now"}
                      </Button>
                    )}
                    {canManageGameTeam(detailsGame.team_id) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setActiveSheet({
                          teamId: detailsGame.team_id,
                          teamName: teams.find(t => t.id === detailsGame.team_id)?.name || "this team",
                        })}
                        title="Choose which players get the game messages"
                        data-testid="who-gets-messaged-btn"
                      >
                        <Users className="w-4 h-4 mr-1" />
                        Who gets messaged
                      </Button>
                    )}
                    {canManageGameTeam(detailsGame.team_id) && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={announcing === detailsGame.id}
                        onClick={() => announceSquad(detailsGame)}
                        className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                        title="Send each selected player their own WhatsApp"
                        data-testid="announce-squad-btn"
                      >
                        <Send className="w-4 h-4 mr-1" />
                        {announcing === detailsGame.id ? "Sending…" : "Announce squad"}
                      </Button>
                    )}
                    {canManageGameTeam(detailsGame.team_id) && (
                      <Button
                        size="sm"
                        onClick={() => openAddPlayerDialog(detailsGame)}
                        className="bg-yellow-500/100 hover:bg-yellow-600 text-black"
                      >
                        Add Player
                      </Button>
                    )}
                  </div>
                </div>
                {(detailsGame.signups || []).length === 0 ? (
                  <p className="text-slate-400 text-sm">No players signed up yet</p>
                ) : (
                  <div className="space-y-2">
                    {[...(detailsGame.signups || [])]
                      .sort((a, b) => ((a.is_guest ? a.name : a.user_name) || '').localeCompare((b.is_guest ? b.name : b.user_name) || ''))
                      .map((signup, index) => (
                      <div key={signup.is_guest ? signup.guest_id : signup.user_id || index} className="flex justify-between items-center p-2 bg-slate-800/60 border border-green-500/30 rounded">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-slate-100 font-medium">
                            {signup.is_guest ? signup.name : signup.user_name}
                          </span>
                          {signup.is_guest && (
                            <span className="text-xs px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded font-semibold">GUEST</span>
                          )}
                          {signup.is_section_guest && (
                            <span className="text-xs px-1.5 py-0.5 bg-cyan-500/20 text-cyan-400 rounded font-semibold" title={`From: ${(signup.home_teams || []).join(', ')}`}>
                              CALL-UP {signup.home_teams?.length > 0 && `(${signup.home_teams[0]})`}
                            </span>
                          )}
                          <span className={`text-xs px-2 py-1 rounded ${
                            signup.signup_type === 'playing' 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {signup.signup_type === 'playing' ? 'Playing' : 'Social/F&B'}
                          </span>
                          {signup.team_source && signup.team_source !== 'primary' && !signup.is_section_guest && (
                            <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded">
                              2nd Team
                            </span>
                          )}
                          <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded font-semibold">
                            SQUAD
                          </span>
                        </div>
                        {canManageGameTeam(detailsGame.team_id) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => removePlayerFromGame(detailsGame.id, signup.is_guest ? signup.guest_id : signup.user_id)}
                            className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Standby List */}
              {(detailsGame.standby || []).length > 0 && (
                <div>
                  <h3 className="font-bold text-white mb-3">
                    Standby List ({(detailsGame.standby || []).length})
                  </h3>
                  <p className="text-xs text-orange-400 mb-3">
                    Players on standby will be promoted to main squad when someone withdraws
                  </p>
                  <div className="space-y-2">
                    {[...(detailsGame.standby || [])]
                      .sort((a, b) => ((a.is_guest ? a.name : a.user_name) || '').localeCompare((b.is_guest ? b.name : b.user_name) || ''))
                      .map((signup, index) => (
                      <div key={signup.is_guest ? signup.guest_id : signup.user_id || index} className="flex justify-between items-center p-2 bg-slate-800/60 border border-orange-500/30 rounded">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-slate-100 font-medium">
                            {signup.is_guest ? signup.name : signup.user_name}
                          </span>
                          {signup.is_guest && (
                            <span className="text-xs px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded font-semibold">G</span>
                          )}
                          <span className={`text-xs px-2 py-1 rounded ${
                            signup.signup_type === 'playing' 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {signup.signup_type === 'playing' ? 'Playing' : 'Social/F&B'}
                          </span>
                          {signup.team_source && signup.team_source !== 'primary' && (
                            <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded">
                              2nd Team
                            </span>
                          )}
                          <span className="text-xs bg-orange-600 text-white px-2 py-0.5 rounded font-semibold">
                            STANDBY
                          </span>
                        </div>
                        {canManageGameTeam(detailsGame.team_id) && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => promoteStandby(detailsGame.id, signup.is_guest ? signup.guest_id : signup.user_id)}
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              Promote
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => removePlayerFromGame(detailsGame.id, signup.is_guest ? signup.guest_id : signup.user_id)}
                              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                            >
                              Remove
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Calendar-Based Unavailable (Auto-excluded based on calendar) */}
              {canManageGameTeam(detailsGame.team_id) && gameAvailability && gameAvailability.unavailable && gameAvailability.unavailable.length > 0 && (
                <div className="border-t border-slate-700 pt-4 mt-4">
                  <h3 className="font-bold text-white mb-3 flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-red-400" />
                    Calendar Conflicts ({gameAvailability.unavailable.length})
                  </h3>
                  <p className="text-xs text-slate-400 mb-3">
                    These players have calendar conflicts at game time (auto-excluded based on their settings)
                  </p>
                  <div className="space-y-2">
                    {gameAvailability.unavailable.map((player, index) => (
                      <div key={index} className="flex justify-between items-center p-2 bg-red-500/10 border border-red-500/30 rounded">
                        <div className="flex items-center gap-2">
                          <span className="text-slate-300 font-medium">{player.name}</span>
                          <span className="text-xs bg-red-500/20 text-red-300 px-2 py-0.5 rounded">
                            {player.source === 'outlook' ? '📧 Outlook' : player.source === 'google' ? '📅 Google' : '📝 Manual'}
                          </span>
                        </div>
                        <span className="text-xs text-red-300 italic max-w-[200px] truncate">
                          {player.reason || 'Busy'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Declined / Unavailable Players */}
              {(detailsGame.declined || []).length > 0 && (
                <div>
                  <h3 className="font-bold text-white mb-3">
                    Unavailable ({(detailsGame.declined || []).length})
                  </h3>
                  <p className="text-xs text-slate-400 mb-3">
                    These players have marked themselves as unavailable for this game
                  </p>
                  <div className="space-y-2">
                    {(detailsGame.declined || []).map((decline, index) => (
                      <div key={index} className="flex justify-between items-center p-2 bg-slate-800/50 border border-slate-700 rounded">
                        <div className="flex items-center gap-2">
                          <span className="text-slate-400 font-medium">{decline.user_name}</span>
                          <span className="text-xs bg-gray-400 text-white px-2 py-0.5 rounded font-semibold">
                            UNAVAILABLE
                          </span>
                        </div>
                        {decline.reason && (
                          <span className="text-xs text-slate-400 italic">&quot;{decline.reason}&quot;</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Player Dialog */}
      <Dialog open={showAddPlayerDialog} onOpenChange={setShowAddPlayerDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="add-player-dialog">
          <DialogHeader>
            <DialogTitle>Add Players to Squad</DialogTitle>
          </DialogHeader>
          
          {/* Tabs */}
          <div className="flex gap-2 border-b mt-4">
            <button
              onClick={() => setAddPlayerTab("registered")}
              className={`px-4 py-2 font-semibold transition-colors ${
                addPlayerTab === "registered"
                  ? "border-b-2 border-yellow-600 text-yellow-400"
                  : "text-slate-400 hover:text-slate-400"
              }`}
            >
              Team Members
            </button>
            <button
              onClick={() => { setAddPlayerTab("section"); setSectionMembers([]); setSelectedSectionPlayerIds([]); }}
              className={`px-4 py-2 font-semibold transition-colors ${
                addPlayerTab === "section"
                  ? "border-b-2 border-blue-600 text-blue-400"
                  : "text-slate-400 hover:text-slate-400"
              }`}
            >
              Section Players
            </button>
            <button
              onClick={() => setAddPlayerTab("guest")}
              className={`px-4 py-2 font-semibold transition-colors ${
                addPlayerTab === "guest"
                  ? "border-b-2 border-yellow-600 text-yellow-400"
                  : "text-slate-400 hover:text-slate-400"
              }`}
            >
              + External Guest
            </button>
          </div>

          <div className="space-y-4 mt-4">
            {addPlayerTab === "registered" && (
              <p className="text-sm text-slate-400">
                Select players to add to the game day squad (for members who don&apos;t use the portal)
              </p>
            )}
            {addPlayerTab === "section" && (
              <p className="text-sm text-blue-400">
                Search and add players from other teams in the section (e.g., call-ups for tough games)
              </p>
            )}
            {addPlayerTab === "guest" && (
              <p className="text-sm text-slate-400">
                Add an external guest player (non-registered person) to the squad
              </p>
            )}

            {/* Content based on tab */}
            {addPlayerTab === "registered" && (
              <>
            {/* Squad Status Info */}
            {addPlayerGame && (
              <div className="bg-slate-800/60 border-2 border-blue-500/30 rounded p-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold text-blue-300">Squad Status:</span>
                  <span className="text-lg font-bold text-blue-300">
                    {(addPlayerGame.signups || []).length}
                    {addPlayerGame.squad_limit && ` / ${addPlayerGame.squad_limit}`} players
                  </span>
                </div>
                {addPlayerGame.squad_limit ? (
                  <>
                    <div className="w-full bg-slate-700 rounded-full h-2 mb-2">
                      <div 
                        className="bg-blue-500/100 h-2 rounded-full transition-all"
                        style={{ 
                          width: `${Math.min(((addPlayerGame.signups || []).length / addPlayerGame.squad_limit) * 100, 100)}%` 
                        }}
                      />
                    </div>
                    {(addPlayerGame.signups || []).length >= addPlayerGame.squad_limit ? (
                      <p className="text-xs text-red-400 font-semibold">
                        ⚠️ Squad is FULL - New players will go to STANDBY list
                      </p>
                    ) : (
                      <p className="text-xs text-blue-300">
                        {addPlayerGame.squad_limit - (addPlayerGame.signups || []).length} spots remaining in main squad
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-xs text-blue-300">No squad limit set - unlimited spots</p>
                )}
                {(addPlayerGame.standby || []).length > 0 && (
                  <p className="text-xs text-orange-400 mt-1">
                    📋 {(addPlayerGame.standby || []).length} player(s) currently on standby
                  </p>
                )}
              </div>
            )}

            {/* Preview where players will go */}
            {selectedPlayerIds.length > 0 && addPlayerGame && addPlayerGame.squad_limit && (
              <div className="bg-slate-800/60 border border-yellow-500/30 rounded p-3 text-sm">
                {(() => {
                  const currentSquadSize = (addPlayerGame.signups || []).length;
                  const spotsLeft = addPlayerGame.squad_limit - currentSquadSize;
                  const toMainSquad = Math.min(selectedPlayerIds.length, Math.max(0, spotsLeft));
                  const toStandby = Math.max(0, selectedPlayerIds.length - spotsLeft);
                  
                  return (
                    <div className="space-y-1">
                      <p className="font-semibold text-yellow-400">Selected players will be added as:</p>
                      {toMainSquad > 0 && (
                        <p className="text-green-400">✓ {toMainSquad} to <strong>Main Squad</strong></p>
                      )}
                      {toStandby > 0 && (
                        <p className="text-orange-400">⏳ {toStandby} to <strong>Standby List</strong></p>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
            
            {/* Signup Type Selection */}
            <div className="bg-slate-800/50 p-3 rounded border">
              <label className="text-sm font-medium mb-2 block">Add as:</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="signupType"
                    value="playing"
                    checked={addPlayerType === "playing"}
                    onChange={(e) => setAddPlayerType(e.target.value)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Playing</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="signupType"
                    value="social_fb_only"
                    checked={addPlayerType === "social_fb_only"}
                    onChange={(e) => setAddPlayerType(e.target.value)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Social/F&B Only</span>
                </label>
              </div>
              <p className="text-xs text-slate-400 mt-2">
                Note: This sets their participation type. Squad vs Standby is determined by the squad limit above.
              </p>
            </div>

            {/* Player Selection */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                Select Players ({selectedPlayerIds.length} selected)
              </label>
              {/* Search Input */}
              <div className="mb-3">
                <Input
                  type="text"
                  placeholder="Search by name or membership number..."
                  value={playerSearchTerm}
                  onChange={(e) => setPlayerSearchTerm(e.target.value)}
                  className="w-full"
                  data-testid="player-search-input"
                />
              </div>
              {teamMembers.length === 0 ? (
                <p className="text-sm text-slate-400 p-4 text-center border rounded">
                  No available players (all team members already in squad)
                </p>
              ) : (
                <div className="border rounded max-h-60 overflow-y-auto">
                  {teamMembers
                    .filter(member => {
                      if (!playerSearchTerm.trim()) return true;
                      const searchLower = playerSearchTerm.toLowerCase();
                      return (
                        member.name?.toLowerCase().includes(searchLower) ||
                        member.membership_number?.toLowerCase().includes(searchLower)
                      );
                    })
                    .map((member) => {
                      // Check if this player has a calendar conflict
                      const unavailPlayer = gameAvailability?.unavailable?.find(p => p.id === member.user_id);
                      const isUnavailable = !!unavailPlayer;
                      
                      return (
                    <label
                      key={member.user_id}
                      className={`flex items-center gap-3 p-3 hover:bg-slate-800/50 cursor-pointer border-b last:border-b-0 ${
                        isUnavailable ? 'bg-red-500/5' : ''
                      }`}
                    >
                      <Checkbox
                        checked={selectedPlayerIds.includes(member.user_id)}
                        onCheckedChange={() => togglePlayerSelection(member.user_id)}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className={`font-medium ${isUnavailable ? 'text-slate-400' : 'text-white'}`}>{member.name}</p>
                          {member.team_source === 'primary' && (
                            <span className="text-xs bg-yellow-500/20 text-yellow-400 px-1.5 py-0.5 rounded font-semibold">1ST</span>
                          )}
                          {member.team_source === 'secondary' && (
                            <span className="text-xs bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded font-semibold">2ND</span>
                          )}
                          {member.team_source?.startsWith('team_') && (
                            <span className="text-xs bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded font-semibold">
                              {member.team_source.replace('team_', '').toUpperCase()}{member.team_source.replace('team_', '') === '3' ? 'RD' : 'TH'}
                            </span>
                          )}
                          {member.team_source === 'other' && (
                            <span className="text-xs bg-slate-500/20 text-slate-400 px-1.5 py-0.5 rounded font-semibold">+</span>
                          )}
                          {isUnavailable && (
                            <span className="text-xs bg-red-500/20 text-red-300 px-1.5 py-0.5 rounded" title={unavailPlayer.reason}>
                              ⚠️ {unavailPlayer.source === 'outlook' ? 'Outlook' : unavailPlayer.source === 'google' ? 'Google' : 'Calendar'} conflict
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <p className="text-xs text-slate-400">{member.membership_number}</p>
                          {isUnavailable && (
                            <p className="text-xs text-red-300 italic truncate max-w-[150px]">{unavailPlayer.reason}</p>
                          )}
                        </div>
                      </div>
                    </label>
                      );
                    })}
                  {teamMembers.filter(member => {
                    if (!playerSearchTerm.trim()) return true;
                    const searchLower = playerSearchTerm.toLowerCase();
                    return (
                      member.name?.toLowerCase().includes(searchLower) ||
                      member.membership_number?.toLowerCase().includes(searchLower)
                    );
                  }).length === 0 && playerSearchTerm.trim() && (
                    <p className="text-sm text-slate-400 p-4 text-center">
                      No players match &quot;{playerSearchTerm}&quot;
                    </p>
                  )}
                </div>
              )}
            </div>

            <Button
              onClick={manuallyAddPlayers}
              disabled={selectedPlayerIds.length === 0}
              className="w-full bg-yellow-500/100 hover:bg-yellow-600 text-black font-semibold disabled:opacity-50"
              data-testid="confirm-add-player-button"
            >
              Add {selectedPlayerIds.length} Player(s) to Squad
            </Button>
            </>
            )}

            {/* Section Players Tab */}
            {addPlayerTab === "section" && (
              <>
                {/* Search Box */}
                <div className="space-y-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search by name, email or membership #..."
                      value={sectionSearchTerm}
                      onChange={(e) => setSectionSearchTerm(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          searchSectionMembers(sectionSearchTerm);
                        }
                      }}
                      className="pl-10 bg-slate-800 border-slate-600"
                    />
                  </div>
                  <Button
                    onClick={() => searchSectionMembers(sectionSearchTerm)}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    disabled={loadingSectionMembers}
                  >
                    {loadingSectionMembers ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Searching...
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        Search Section Members
                      </>
                    )}
                  </Button>
                </div>

                {/* Search Results */}
                {sectionMembers.length > 0 && (
                  <div className="border rounded-lg border-slate-700 max-h-64 overflow-y-auto">
                    {sectionMembers.map((member) => (
                      <label
                        key={member.user_id}
                        className="flex items-center gap-3 p-3 hover:bg-slate-800/50 cursor-pointer border-b last:border-b-0"
                      >
                        <Checkbox
                          checked={selectedSectionPlayerIds.includes(member.user_id)}
                          onCheckedChange={() => toggleSectionPlayerSelection(member.user_id)}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-white">{member.name}</p>
                            {member.has_telegram && (
                              <span className="text-xs bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded">Telegram</span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {member.teams?.map((team, idx) => (
                              <span key={idx} className="text-xs bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded">
                                {team.position} Team: {team.team_name}
                              </span>
                            ))}
                            {(!member.teams || member.teams.length === 0) && (
                              <span className="text-xs text-slate-400">Section Member (no team)</span>
                            )}
                          </div>
                          {member.membership_number && (
                            <p className="text-xs text-slate-500 mt-1">{member.membership_number}</p>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                )}

                {sectionMembers.length === 0 && sectionSearchTerm && !loadingSectionMembers && (
                  <p className="text-sm text-slate-400 text-center py-4">
                    No results. Try searching by name or membership number.
                  </p>
                )}

                {/* Player Type Selection */}
                {selectedSectionPlayerIds.length > 0 && (
                  <div className="space-y-2 bg-slate-800/50 rounded p-3">
                    <p className="text-sm font-medium text-slate-300">Add selected as:</p>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="section_signup_type"
                          value="playing"
                          checked={addPlayerType === "playing"}
                          onChange={(e) => setAddPlayerType(e.target.value)}
                          className="accent-yellow-500"
                        />
                        <span className="text-sm text-white">Playing</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="section_signup_type"
                          value="injured"
                          checked={addPlayerType === "injured"}
                          onChange={(e) => setAddPlayerType(e.target.value)}
                          className="accent-yellow-500"
                        />
                        <span className="text-sm text-white">Injured</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="section_signup_type"
                          value="other"
                          checked={addPlayerType === "other"}
                          onChange={(e) => setAddPlayerType(e.target.value)}
                          className="accent-yellow-500"
                        />
                        <span className="text-sm text-white">Other</span>
                      </label>
                    </div>
                  </div>
                )}

                <div className="bg-blue-500/10 border border-blue-500/30 rounded p-3 text-sm">
                  <p className="text-blue-300 font-medium mb-1">ℹ️ Section Player Call-up</p>
                  <ul className="text-blue-200/80 text-xs space-y-1 list-disc list-inside">
                    <li>These players will be marked as &quot;GUEST&quot; in the squad</li>
                    <li>They will receive pre-game reminders and score updates</li>
                    <li>They will NOT become permanent team members</li>
                  </ul>
                </div>

                <Button
                  onClick={addSectionPlayers}
                  disabled={selectedSectionPlayerIds.length === 0}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold disabled:opacity-50"
                >
                  Add {selectedSectionPlayerIds.length} Section Player(s) as Guest
                </Button>
              </>
            )}

            {/* Guest Player Tab */}
            {addPlayerTab === "guest" && (
              <>
                {/* Squad Status Info */}
                {addPlayerGame && (
                  <div className="bg-slate-800/60 border-2 border-blue-500/30 rounded p-3">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-semibold text-blue-300">Squad Status:</span>
                      <span className="text-lg font-bold text-blue-300">
                        {(addPlayerGame.signups || []).length}
                        {addPlayerGame.squad_limit && ` / ${addPlayerGame.squad_limit}`} players
                      </span>
                    </div>
                  </div>
                )}

                {/* Signup Type Selection */}
                <div className="bg-slate-800/50 p-3 rounded border">
                  <label className="text-sm font-medium mb-2 block">Add as:</label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="guestSignupType"
                        value="playing"
                        checked={addPlayerType === "playing"}
                        onChange={(e) => setAddPlayerType(e.target.value)}
                      />
                      <span className="text-sm">Playing</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="guestSignupType"
                        value="social"
                        checked={addPlayerType === "social"}
                        onChange={(e) => setAddPlayerType(e.target.value)}
                      />
                      <span className="text-sm">Social / F&B</span>
                    </label>
                  </div>
                </div>

                {/* Guest Player Form */}
                <div className="space-y-4 bg-slate-900/50 border rounded p-4">
                  <div>
                    <label className="text-sm font-semibold text-slate-400 mb-1 block">
                      Guest Name *
                    </label>
                    <Input
                      value={guestPlayerName}
                      onChange={(e) => setGuestPlayerName(e.target.value)}
                      placeholder="Enter guest player name"
                      required
                    />
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-slate-400 mb-1 block">
                      Phone (Optional)
                    </label>
                    <Input
                      value={guestPlayerPhone}
                      onChange={(e) => setGuestPlayerPhone(e.target.value)}
                      placeholder="+65 1234 5678"
                      type="tel"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-slate-400 mb-1 block">
                      Email (Optional)
                    </label>
                    <Input
                      value={guestPlayerEmail}
                      onChange={(e) => setGuestPlayerEmail(e.target.value)}
                      placeholder="guest@example.com"
                      type="email"
                    />
                  </div>
                </div>

                <Button
                  onClick={addGuestPlayer}
                  disabled={!guestPlayerName.trim() || addingGuest}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold disabled:opacity-50"
                >
                  {addingGuest ? "Adding..." : "Add Guest Player"}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Logistics Success Dialog with WhatsApp Export */}
      <Dialog open={showLogisticsSuccess} onOpenChange={setShowLogisticsSuccess}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-green-400 flex items-center gap-2">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Logistics Request Sent!
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-slate-300">
              Your game has been created and the logistics request has been sent to SCC Sports Staff via Telegram.
            </p>
            
            <div className="bg-slate-900/50 rounded-lg p-4">
              <p className="text-sm text-slate-400 mb-3">
                Want to share this request via WhatsApp too?
              </p>
              <Button
                onClick={copyLogisticsToWhatsApp}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Copy for WhatsApp
              </Button>
            </div>
            
            <Button
              onClick={() => setShowLogisticsSuccess(false)}
              variant="outline"
              className="w-full border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Logistics Management Dialog */}
      <Dialog open={showLogisticsDialog} onOpenChange={setShowLogisticsDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {gameLogistics?.id ? '📋 Edit Logistics Request' : '📋 Submit Logistics Request'}
            </DialogTitle>
          </DialogHeader>
          {logisticsGame && (
            <div className="space-y-4 mt-4">
              {/* Game Info Summary */}
              <div className="bg-slate-800/60 rounded-lg p-4 border border-slate-700">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-slate-400">Team:</span>
                    <span className="text-white ml-2">{teams.find(t => t.id === logisticsGame.team_id)?.name}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Date:</span>
                    <span className="text-white ml-2">{new Date(logisticsGame.date).toLocaleDateString()}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Venue:</span>
                    <span className="text-white ml-2">{logisticsGame.venue || logisticsGame.location}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Opponent:</span>
                    <span className="text-white ml-2">{logisticsGame.opponent || 'TBD'}</span>
                  </div>
                </div>
                {logisticsGame.logistics_request?.id && (
                  <div className="mt-3 pt-3 border-t border-slate-600">
                    <span className={`text-xs px-2 py-1 rounded ${
                      logisticsGame.logistics_request?.status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                      logisticsGame.logistics_request?.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-slate-500/20 text-slate-400'
                    }`}>
                      Status: {logisticsGame.logistics_request?.status?.toUpperCase() || 'PENDING'}
                    </span>
                  </div>
                )}
              </div>

              {loadingLogistics ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-yellow-400" />
                  <span className="ml-2 text-slate-400">Loading logistics...</span>
                </div>
              ) : (
                <>
                  <LogisticsForm 
                    teamId={logisticsGame.team_id}
                    user={user}
                    initialData={gameLogistics}
                    onChange={setGameLogistics}
                  />
                  
                  {/* Submit Button */}
                  <div className="flex gap-3 pt-4 border-t border-slate-700">
                    <Button
                      onClick={() => setShowLogisticsDialog(false)}
                      variant="outline"
                      className="flex-1 border-slate-600 text-slate-300"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => saveGameLogistics(gameLogistics)}
                      disabled={savingLogistics}
                      className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white"
                    >
                      {savingLogistics ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        logisticsGame.logistics_request?.id ? 'Update & Notify Staff' : 'Submit & Notify Staff'
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Game Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent data-testid="edit-game-dialog">
          <DialogHeader>
            <DialogTitle>Edit/Postpone Game</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Team</label>
              <select
                value={editTeamId}
                onChange={(e) => setEditTeamId(e.target.value)}
                className="w-full p-2 border rounded"
              >
                <option value="">Select team</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Date & Time</label>
              <Input
                type="datetime-local"
                value={editDate}
                onChange={(e) => setEditDate(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Location</label>
              <Input
                placeholder="Game venue"
                value={editLocation}
                onChange={(e) => setEditLocation(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Opponent</label>
              <Input
                placeholder="Opponent team name"
                value={editOpponent}
                onChange={(e) => setEditOpponent(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Squad Limit (Optional)</label>
              <Input
                type="number"
                placeholder="e.g., 18"
                value={editSquadLimit}
                onChange={(e) => setEditSquadLimit(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Standby Promotion</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="editStandbyPromotion"
                    value="manual"
                    checked={editStandbyPromotion === "manual"}
                    onChange={(e) => setEditStandbyPromotion(e.target.value)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Manual</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="editStandbyPromotion"
                    value="auto"
                    checked={editStandbyPromotion === "auto"}
                    onChange={(e) => setEditStandbyPromotion(e.target.value)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Auto</span>
                </label>
              </div>
            </div>
            <Button
              onClick={updateGame}
              className="w-full bg-yellow-500/100 hover:bg-yellow-600 text-black font-semibold"
            >
              Update Game
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Complete Game Dialog */}
      <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">Complete Game & Record Stats</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 sm:space-y-6 mt-4">
            {/* Scores */}
            <div className="grid grid-cols-2 gap-2 sm:gap-4">
              <div>
                <label className="text-xs sm:text-sm font-medium mb-1 sm:mb-2 block">Home Score</label>
                <Input
                  type="number"
                  min="0"
                  value={completeScoreHome}
                  onChange={(e) => {
                    const score = e.target.value;
                    setCompleteScoreHome(score);
                    
                    // Auto-populate goal scorer fields based on home score
                    const numGoals = parseInt(score) || 0;
                    if (numGoals >= 0) {
                      const newScorers = [];
                      for (let i = 0; i < numGoals; i++) {
                        // Keep existing data if available, otherwise create new entry with 1 assist field
                        newScorers.push(goalScorers[i] || { 
                          user_id: "", 
                          user_name: "", 
                          assists: [{ user_id: "", user_name: "" }] // Auto-populate 1 assist field
                        });
                      }
                      setGoalScorers(newScorers);
                    }
                  }}
                  placeholder="0"
                />
                <p className="text-xs text-slate-400 mt-1 hidden sm:block">Goal scorers auto-populate below</p>
              </div>
              <div>
                <label className="text-xs sm:text-sm font-medium mb-1 sm:mb-2 block">Away Score</label>
                <Input
                  type="number"
                  min="0"
                  value={completeScoreAway}
                  onChange={(e) => setCompleteScoreAway(e.target.value)}
                  placeholder="0"
                />
              </div>
            </div>

            {/* Goal Scorers */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium">⚽ Goal Scorers & Assists</label>
                {goalScorers.length > 0 && (
                  <span className="text-xs text-slate-400 bg-slate-800 px-2 py-1 rounded">
                    {goalScorers.length} {goalScorers.length === 1 ? 'goal' : 'goals'}
                  </span>
                )}
              </div>
              
              {goalScorers.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4 bg-slate-800/50 rounded border-2 border-dashed">
                  Enter home score above to auto-populate goal scorer fields
                </p>
              ) : (
                <div className="space-y-4">
                  {goalScorers.map((scorer, scorerIdx) => (
                    <Card key={scorerIdx} className="p-4 bg-slate-800/50">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-bold text-slate-400">⚽ Goal #{scorerIdx + 1}</h4>
                        </div>
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <label className="text-xs font-medium mb-1 block">Scorer</label>
                            <Select
                              value={scorer.user_id}
                              onValueChange={(value) => {
                                const eligibleScorers = getEligibleScorers(completeGame);
                                const selected = eligibleScorers.find(s => s.id === value);
                                updateGoalScorer(scorerIdx, value, selected?.name || "");
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select player" />
                              </SelectTrigger>
                              <SelectContent>
                                {(() => {
                                  const scorers = getEligibleScorers(completeGame);
                                  return scorers.length > 0 ? (
                                    scorers.map(s => (
                                      <SelectItem key={s.id} value={s.id}>
                                        {s.displayName}
                                      </SelectItem>
                                    ))
                                  ) : (
                                    <SelectItem value="no_players" disabled>No players signed up</SelectItem>
                                  );
                                })()}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {/* Assists for this goal */}
                        <div className="ml-4 space-y-2">
                          <div className="flex items-center justify-between">
                            <label className="text-xs font-medium text-slate-400">Assist (leave empty if none)</label>
                            {scorer.assists.length < 2 && (
                              <Button
                                onClick={() => addAssist(scorerIdx)}
                                size="sm"
                                variant="ghost"
                                className="text-xs h-6"
                              >
                                + Add 2nd Assist
                              </Button>
                            )}
                          </div>
                          
                          {scorer.assists.map((assist, assistIdx) => (
                            <div key={assistIdx} className="flex gap-2">
                              <div className="flex-1">
                                <Select
                                  value={assist.user_id}
                                  onValueChange={(value) => {
                                    const eligibleScorers = getEligibleScorers(completeGame, scorer.user_id);
                                    const selected = eligibleScorers.find(s => s.id === value);
                                    updateAssist(scorerIdx, assistIdx, value, selected?.name || "");
                                  }}
                                >
                                  <SelectTrigger className="h-8">
                                    <SelectValue placeholder="Select assisting player" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {(() => {
                                      const scorers = getEligibleScorers(completeGame, scorer.user_id);
                                      return scorers.length > 0 ? (
                                        scorers.map(s => (
                                          <SelectItem key={s.id} value={s.id}>
                                            {s.displayName}
                                          </SelectItem>
                                        ))
                                      ) : (
                                        <SelectItem value="no_players" disabled>No other players available</SelectItem>
                                      );
                                    })()}
                                  </SelectContent>
                                </Select>
                              </div>
                              <Button
                                onClick={() => removeAssist(scorerIdx, assistIdx)}
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-400 h-8 px-2"
                              >
                                ✕
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* MOTM */}
            <div>
              <label className="text-sm font-medium mb-2 block">🏆 Man of the Match</label>
              <Select value={motmPlayer} onValueChange={setMotmPlayer}>
                <SelectTrigger>
                  <SelectValue placeholder="Select MOTM (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {completeGame?.signups?.filter(s => s.signup_type === 'playing').length > 0 ? (
                    completeGame.signups.filter(s => s.signup_type === 'playing').map(signup => (
                      <SelectItem key={signup.user_id} value={signup.user_id}>
                        {signup.user_name}
                      </SelectItem>
                    ))
                  ) : null}
                </SelectContent>
              </Select>
            </div>

            {/* Match Notes */}
            <div>
              <label className="text-sm font-medium mb-2 block">Match Notes (Optional)</label>
              <Textarea
                value={matchNotes}
                onChange={(e) => setMatchNotes(e.target.value)}
                placeholder="Add any notes about the match (key moments, injuries, etc.)"
                rows={4}
              />
            </div>

            {/* Warning */}
            <div className="bg-slate-800/60 border border-yellow-500/30 rounded p-3">
              <p className="text-sm text-yellow-400">
                <strong>Note:</strong> Completing this game will:
              </p>
              <ul className="text-sm text-yellow-300 mt-2 ml-4 list-disc space-y-1">
                <li>Close signups and lock game details</li>
                <li>Update player stats (goals, assists, MOTM)</li>
                <li>Allow you to add a detailed match report afterward</li>
              </ul>
            </div>

            {/* Actions */}
            <div className="flex gap-2 justify-end">
              <Button
                onClick={() => setShowCompleteDialog(false)}
                variant="ghost"
                disabled={completing}
              >
                Cancel
              </Button>
              <Button
                onClick={submitGameCompletion}
                disabled={completing || !completeScoreHome || !completeScoreAway}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {completing ? "Completing..." : "Complete Game"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Charge Sheet Dialog */}
      <Dialog open={showChargeSheet && chargeSheetGame} onOpenChange={(open) => {
        if (!open) {
          setShowChargeSheet(false);
          setChargeSheetGame(null);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0 sm:rounded-lg" data-testid="charge-sheet-dialog">
          {chargeSheetGame && (
            <ChargeSheet
              game={chargeSheetGame}
              user={user}
              onClose={() => {
                setShowChargeSheet(false);
                setChargeSheetGame(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Attendance Dialog */}
      <Dialog open={showAttendanceDialog} onOpenChange={setShowAttendanceDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">📋 Mark Attendance</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 sm:space-y-4 mt-4">
            <p className="text-xs sm:text-sm text-slate-400">
              Mark who attended. Only "Present" will be charged.
            </p>
            
            {/* Attendance List */}
            <div className="space-y-2 max-h-[350px] sm:max-h-[400px] overflow-y-auto">
              {attendanceList.map((player, index) => (
                <div 
                  key={player.user_id} 
                  className={`flex items-center justify-between p-2 sm:p-3 rounded border ${
                    player.status === 'present' || player.status === 'late_addition'
                      ? 'border-green-500/30 bg-green-500/10' 
                      : 'border-red-500/30 bg-red-500/10'
                  }`}
                >
                  <div className="flex-1 min-w-0 mr-2">
                    <p className="font-medium text-sm sm:text-base truncate">{player.user_name}</p>
                    <p className="text-xs text-slate-400 truncate">
                      {player.membership_number || "No #"}
                      {player.status === 'late_addition' && (
                        <span className="ml-1 text-yellow-400">• Late</span>
                      )}
                    </p>
                  </div>
                  <Button
                    onClick={() => toggleAttendanceStatus(index)}
                    variant={player.status === 'present' || player.status === 'late_addition' ? "default" : "outline"}
                    size="sm"
                    className={`text-xs sm:text-sm whitespace-nowrap ${
                      player.status === 'present' || player.status === 'late_addition'
                        ? "bg-green-600 hover:bg-green-700"
                        : "border-red-500 text-red-400"
                    }`}
                  >
                    {player.status === 'present' || player.status === 'late_addition' ? "✓" : "✗"}
                    <span className="hidden sm:inline ml-1">{player.status === 'present' || player.status === 'late_addition' ? "Present" : "Absent"}</span>
                  </Button>
                </div>
              ))}
            </div>

            {/* Add Late Player */}
            <div className="border-t border-slate-700 pt-4">
              <p className="text-sm font-medium mb-2">Add Late Player</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  placeholder="Player name"
                  value={addPlayerName}
                  onChange={(e) => setAddPlayerName(e.target.value)}
                  className="flex-1"
                />
                <div className="flex gap-2">
                  <Input
                    placeholder="Membership #"
                    value={addPlayerMembership}
                    onChange={(e) => setAddPlayerMembership(e.target.value)}
                    className="w-28 sm:w-32"
                  />
                  <Button onClick={addLatePlayer} variant="outline" size="sm">
                    + Add
                  </Button>
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="bg-slate-800/50 rounded p-3 text-sm">
              <p>
                <span className="text-green-400 font-medium">
                  {attendanceList.filter(p => p.status === 'present' || p.status === 'late_addition').length} Present
                </span>
                {' • '}
                <span className="text-red-400 font-medium">
                  {attendanceList.filter(p => p.status === 'absent').length} Absent
                </span>
                {attendanceList.filter(p => p.status === 'late_addition').length > 0 && (
                  <>
                    {' • '}
                    <span className="text-yellow-400 font-medium">
                      {attendanceList.filter(p => p.status === 'late_addition').length} Late Additions
                    </span>
                  </>
                )}
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-2 justify-end">
              <Button
                onClick={() => setShowAttendanceDialog(false)}
                variant="ghost"
                disabled={savingAttendance}
              >
                Cancel
              </Button>
              <Button
                onClick={saveAttendance}
                disabled={savingAttendance}
                className="bg-green-600 hover:bg-green-700"
              >
                {savingAttendance ? "Saving..." : "Save Attendance"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Goal Scorers Dialog */}
      <Dialog open={showEditScorersDialog} onOpenChange={setShowEditScorersDialog}>
        <DialogContent
          className="max-w-2xl max-h-[80vh] overflow-y-auto"
          // Escape should shut the name list, not the whole dialog and every
          // unsaved goal with it.
          onEscapeKeyDown={(e) => {
            if (scorerPickerOpen) {
              e.preventDefault();
              setScorerPickerOpen(false);
            }
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-400" />
              Edit Goal Scorers
            </DialogTitle>
          </DialogHeader>
          
          {editScorersGame && (
            <div className="space-y-4">
              {/* Game Info */}
              <div className="p-3 bg-slate-800 rounded-lg">
                <p className="font-semibold text-white">
                  vs {editScorersGame.opponent}
                </p>
                <p className="text-sm text-slate-400">
                  {new Date(editScorersGame.date).toLocaleDateString()} • Score: {editScorersGame.score_home} - {editScorersGame.score_away}
                </p>
              </div>
              
              {/* Current scorers summary — what was on the game when this opened */}
              {editScorersGame.goal_scorers?.length > 0 && (
                <details className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <summary className="text-sm text-green-400 font-medium cursor-pointer">
                    What was recorded before this edit
                  </summary>
                  <p className="text-sm text-slate-300 mt-2">
                    {editScorersGame.goal_scorers
                      .map(s => `${s.user_name || "— no name recorded —"} (${s.goals}G, ${s.assists}A)`)
                      .join(", ")}
                  </p>
                </details>
              )}
              
              {/* Who scored — pick by typing, no scrolling */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-300">
                  Start typing a name, pick the player, then set the goals.
                </p>

                <div className="relative">
                  <Input
                    value={scorerQuery}
                    onChange={(e) => { setScorerQuery(e.target.value); setScorerPickerOpen(true); }}
                    onFocus={() => setScorerPickerOpen(true)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const matches = scorerSuggestions;
                        if (matches.length > 0) addScorerFromSquad(matches[0]);
                        else if (creditedMatchIndex >= 0) bumpCreditedGoal(creditedMatchIndex);
                        else if (scorerQuery.trim()) addManualScorer(scorerQuery.trim());
                      }
                      if (e.key === "Escape") setScorerPickerOpen(false);
                    }}
                    placeholder="Type a name to add a scorer…"
                    className="h-10 bg-slate-900 border-slate-700"
                    data-testid="scorer-search"
                  />
                  {scorerPickerOpen && (
                    <>
                      {/* Click-away, so the list closes without stealing the tap */}
                      <div className="fixed inset-0 z-10" onClick={() => setScorerPickerOpen(false)} />
                      <div
                        className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border
                                   border-slate-700 bg-slate-900 shadow-xl"
                        data-testid="scorer-suggestions"
                      >
                        {scorerSuggestions.map((c) => (
                          <button
                            key={c.user_id || c.user_name}
                            onClick={() => addScorerFromSquad(c)}
                            className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left
                                       text-sm text-white hover:bg-slate-800"
                            data-testid={`scorer-option-${c.user_id || c.user_name}`}
                          >
                            <span className="break-words">{c.user_name}</span>
                            <span className="shrink-0 text-xs text-slate-400">
                              {c.isGuest ? "Guest"
                                : (c.signupType || "").startsWith("social") ? "Social/F&B"
                                : c.signupType === "playing" ? "Playing"
                                : (c.signupType || "").replace(/_/g, " ")}
                            </span>
                          </button>
                        ))}
                        {scorerSuggestions.length === 0 && (
                          creditedMatchIndex >= 0 ? (
                            <button
                              onClick={() => bumpCreditedGoal(creditedMatchIndex)}
                              className="w-full px-3 py-2 text-left text-sm text-yellow-300 hover:bg-slate-800"
                              data-testid="scorer-bump-credited"
                            >
                              {editScorersData[creditedMatchIndex].user_name} is already on the
                              sheet — add another goal
                            </button>
                          ) : (
                            <button
                              onClick={() => addManualScorer(scorerQuery.trim())}
                              disabled={!scorerQuery.trim()}
                              className="w-full px-3 py-2 text-left text-sm text-amber-300 hover:bg-slate-800
                                         disabled:text-slate-500"
                              data-testid="scorer-add-typed-name"
                            >
                              {scorerQuery.trim()
                                ? `Add “${scorerQuery.trim()}” — not on the teamsheet`
                                : "Everyone on the teamsheet is already listed"}
                            </button>
                          )
                        )}
                      </div>
                    </>
                  )}
                  <p className="mt-1 text-xs text-slate-500">
                    {scorerCandidates.length} on the teamsheet, including Social/F&amp;B and call-ups.
                  </p>
                </div>

                {editScorersData.length > 0 && (
                  <div className="grid grid-cols-[1fr_56px_56px_28px] sm:grid-cols-[1fr_80px_80px_32px] gap-2 text-xs text-slate-400 font-medium px-2 pt-2">
                    <span>Scorer</span>
                    <span className="text-center">Goals</span>
                    <span className="text-center">Assists</span>
                    <span />
                  </div>
                )}
                
                {editScorersData.map((scorer, idx) => (
                  <div 
                    key={scorer.user_id || `row-${idx}`}
                    className={`grid grid-cols-[1fr_56px_56px_28px] sm:grid-cols-[1fr_80px_80px_32px] gap-2 items-center p-2 rounded ${
                      (scorer.goals > 0 || scorer.assists > 0) ? 'bg-yellow-500/10 border border-yellow-500/30' : 'bg-slate-800/50'
                    }`}
                    data-testid={`scorer-row-${idx}`}
                  >
                    {scorer.fromSquad && (scorer.user_name || "").trim() ? (
                      <span className="text-sm text-white break-words">
                        {scorer.user_name}
                        {scorer.isGuest && (
                          <span className="ml-1 text-xs px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded">G</span>
                        )}
                      </span>
                    ) : (
                      <Input
                        value={scorer.user_name}
                        onChange={(e) => updateEditScorer(idx, 'user_name', e.target.value)}
                        placeholder="Name of the scorer"
                        // A ring, not a border: a global rule forces input
                        // border-color with !important, so a red border is invisible.
                        className={`h-8 bg-slate-900 border-slate-700 text-sm ${
                          !(scorer.user_name || "").trim() && (scorer.goals > 0 || scorer.assists > 0)
                            ? 'ring-1 ring-red-500' : ''}`}
                        data-testid={`scorer-name-${idx}`}
                      />
                    )}
                    <Input
                      type="number"
                      min="0"
                      value={scorer.goals}
                      onChange={(e) => updateEditScorer(idx, 'goals', e.target.value)}
                      className="h-8 text-center bg-slate-900 border-slate-700"
                      data-testid={`scorer-goals-${idx}`}
                    />
                    <Input
                      type="number"
                      min="0"
                      value={scorer.assists}
                      onChange={(e) => updateEditScorer(idx, 'assists', e.target.value)}
                      className="h-8 text-center bg-slate-900 border-slate-700"
                      data-testid={`scorer-assists-${idx}`}
                    />
                    <button
                      onClick={() => removeScorerRow(idx)}
                      title="Take this player off the scoresheet"
                      className="text-slate-500 hover:text-red-400 transition-colors flex justify-center"
                      data-testid={`scorer-remove-${idx}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}

                {editScorersData.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-4" data-testid="no-squad-msg">
                    Nobody is credited yet. Type a name above to add the first scorer.
                  </p>
                )}
              </div>
              
              {/* Summary */}
              {(() => {
                const goals = editScorersData.reduce((sum, s) => sum + (s.goals || 0), 0);
                const assists = editScorersData.reduce((sum, s) => sum + (s.assists || 0), 0);
                const scored = editScorersGame.score_home;
                const unassigned = typeof scored === "number" ? scored - goals : null;
                return (
                  <div className="p-3 bg-slate-800 rounded-lg" data-testid="scorer-totals">
                    <p className="text-sm text-slate-400">
                      Total: {goals} goals, {assists} assists
                    </p>
                    {unassigned !== null && unassigned !== 0 && (
                      <p className={`text-sm mt-1 ${unassigned > 0 ? "text-amber-300" : "text-red-400"}`}
                        data-testid="scorer-mismatch">
                        {unassigned > 0
                          ? `${unassigned} of the ${scored} goals ${unassigned === 1 ? "has" : "have"} nobody against ${unassigned === 1 ? "it" : "them"} yet`
                          : `That is ${Math.abs(unassigned)} more than the ${scored} scored — check the numbers`}
                      </p>
                    )}
                  </div>
                );
              })()}
              
              {/* Actions */}
              <div className="flex gap-2 justify-end">
                <Button
                  onClick={() => setShowEditScorersDialog(false)}
                  variant="ghost"
                  disabled={savingScorers}
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveEditedScorers}
                  disabled={savingScorers}
                  className="bg-yellow-500 hover:bg-yellow-600 text-black"
                >
                  {savingScorers ? "Saving..." : "Save Scorers"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ActiveSquadSheet
        open={!!activeSheet}
        teamId={activeSheet?.teamId}
        teamName={activeSheet?.teamName}
        onClose={() => { setActiveSheet(null); fetchReach(); }}
      />
    </div>
  );
}